/**
 * DecisionFlow — FinanceShell
 * ui/src/shells/FinanceShell.jsx
 * S73-UI-06
 *
 * Shell for user_type = "finance". Route root: /finance/
 *
 * Seven panels (spec v1.3 §3.2):
 *   /finance/alignment        — Financial Alignment
 *   /finance/plan-inputs      — Plan Inputs
 *   /finance/calculation-runs — Calculation Runs
 *   /finance/statements       — Statement Review
 *   /finance/payroll          — Payroll Review (read-only — comp_admin creates/approves)
 *   /finance/reconciliation   — Reconciliation
 *   /finance/disputes         — Disputes (aggregate only — CC-1)
 *
 * S393 changes (OI-S392-01):
 *   ScenarioPlanner stub replaced with full implementation.
 *   Finance creates scenario sets (P50/P75/P90) per period.
 *   Comp_admin approves — NN-36 cross-role actor separation.
 *   Pending approval state surfaced clearly to finance user.
 *
 * Architecture rules:
 *   DD-04: No cross-shell imports. Shared/ only.
 *   DD-05: Role always from JWT user_type.
 *   DD-06: Every value from live API. No hardcoded display values in prod.
 *
 * CC-1 Finance aggregate restriction (api_contract v1.10):
 *   Finance NEVER sees individual rep payout figures.
 *   payout-summaries: aggregate shape only (no per-rep breakdown).
 *   disputes/exceptions: aggregate counts only — no individual record access.
 *   positions: aggregate counts only.
 *   This is server-enforced. The UI must not attempt to display individual
 *   rep data — doing so would surface a 403 and indicate a design error.
 *
 * API endpoints used (api_contract v1.10):
 *   GET  /api/v1/financial-alignment         — §17.3, sensitive read logged
 *   POST /api/v1/financial-plan-inputs       — §17.1, finance only
 *   POST /api/v1/financial-plan-inputs/{id}/approve — §17.2, finance only
 *   GET  /api/v1/calculation-runs            — §11, finance allowed
 *   GET  /api/v1/statement-batches           — §12-S, finance allowed
 *   GET  /api/v1/disputes                    — §18.2, aggregate only (CC-1)
 *   GET  /api/v1/fiscal-periods?period_status=open — active period
 *   GET  /api/v1/scenario-sets               — finance + comp_admin read
 *   POST /api/v1/scenario-sets               — finance create (S393)
 *   POST /api/v1/scenario-sets/{id}/definitions — finance add definitions (S393)
 *
 * Payroll (§16): comp_admin only for all write operations.
 *   Finance panel shows read-only view of export status if endpoint exists.
 *   No create/approve/export actions rendered for finance.
 *
 * Copilot: inline, not panel (spec §4.2, §7). Standard+ tier only.
 *   Read + explain only — no write suggestions (spec §7).
 */

import { useState, useEffect, useRef } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import {
  Layers3, FileCheck2, Calculator, FileText, WalletCards,
  ClipboardCheck, AlertCircle, AlertTriangle, ChevronDown, ChevronUp, Sparkles,
  TrendingUp, BarChart2, History, Layers,
  ShieldAlert, Eye, CheckCircle2, XCircle, Clock, Camera,
  LayoutDashboard, BookOpen, CheckSquare,
} from "lucide-react";

import ExposureMonitor from "../components/finance/ExposureMonitor.jsx";
// S1039: ScenarioSetBuilder wired into Finance shell (S393 — finance creates, CA approves).
// Previously only rendered in Comp Admin → Admin → Scenario Sets tab.
import ScenarioSetBuilder from "../components/tenant-admin/ScenarioSetBuilder.jsx";

// S801 — New-architecture finance components (dedicated routes, old panels untouched)
import CompFinancialControl  from "../components/finance/CompFinancialControl.jsx";
import PeriodCloseWorkflow   from "../components/finance/PeriodCloseWorkflow.jsx";
import SettlementLedger      from "../components/finance/SettlementLedger.jsx";
import AmendmentBeginModal from "../components/admin/AmendmentBeginModal.jsx";
import { useAuth } from "../auth/AuthContext.jsx";
import Button from "../components/shared/Button.jsx";
import Card from "../components/shared/Card.jsx";
import CopilotPanel from "../components/shared/CopilotPanel.jsx";
import Field from "../components/shared/Field.jsx";
import Pill from "../components/shared/Pill.jsx";
import TopBar from "../components/shared/TopBar.jsx";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Auth header helper
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Shared fetch hook
// ---------------------------------------------------------------------------

function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Loading / error primitives
// ---------------------------------------------------------------------------

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

function fmt(val, currency = "USD") {
  if (val == null) return "—";
  return Number(val).toLocaleString("en-US", { style: "currency", currency });
}

function fmtPct(val) {
  if (val == null) return "—";
  return `${(Number(val) * 100).toFixed(1)}%`;
}



// ---------------------------------------------------------------------------
// ApprovalBadge — maps approval_status → Pill tone
// Field: financial_plan_input.approval_status (api_contract §17.0)
// Values: 'draft' | 'approved' | 'rejected' | 'superseded'
// ---------------------------------------------------------------------------

const APPROVAL_BADGE = {
  draft:      { tone: "Draft",     label: "Draft" },
  submitted:  { tone: "Warning",   label: "Submitted — Awaiting Comp Admin Approval" },
  approved:   { tone: "Complete",  label: "Approved" },
  superseded: { tone: "Not Ready", label: "Superseded" },
};

function ApprovalBadge({ status }) {
  if (!status) return <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">—</span>;
  const cfg = APPROVAL_BADGE[status] ?? { tone: "Draft", label: status };
  return <Pill tone={cfg.tone}>{cfg.label}</Pill>;
}




// ---------------------------------------------------------------------------
// Page: PeriodDashboard — /finance/dashboard
//
// S608 Step 2. CFO landing page. Replaces /finance/alignment as default.
//
// Headline strip: 2 rows × 3 tiles
//   Row 1 — Revenue: Revenue Target | Est. Revenue (Est.) | Attainment (Est.)
//   Row 2 — Variable Comp: VC Target | Actual Payout* | % of Target
//   *"Actual Payout" when batch released. "Projected Payout (Est.)" with amber
//    pill when no released batch.
//
// Role breakdown table (centrepiece):
//   Client-side filters: Role | Segment | BU (dropdowns, no new API calls)
//   Columns: Role | HC Plan | HC Actual | AOP Plan VC | Actual Payout |
//            % of AOP | Quota Loaded | vs Plan
//   Total row: VC Target ($1.62M) | Actual vs Budget — Finance Budget total
//   row ONLY (Option B, product owner decision S607).
//
// Data sources (all shell-level, no internal fetches):
//   allPlanInputs   → Revenue Target, Variable Comp Budget, over_assignment_ratio
//   allAlignmentData → total_credits_reporting (→ Est. Revenue)
//   allBatches      → actual payout from released batch
//   allAopData      → HC Plan, AOP Plan VC per role_code
//   allExposureByRole → HC Actual, Actual Payout per role_code
//   allPlanVsLoaded → Quota Loaded, vs Plan per role_code
//
// Join key: role_code across aop-inputs, scenario-exposure/by-role,
//   plan-vs-loaded — confirmed clean (mastercontext S607).
//
// Est. note — always visible beneath table, non-dismissable.
// Line charts — DROPPED (product owner S607).
// Population Scope — RETIRED. Redirects to /finance/dashboard.
// ---------------------------------------------------------------------------

function PeriodDashboard({
  period,
  allBatches,
  allPlanInputs,
  allAopData,
  allAlignmentData,
  allExposureByRole,
  allPlanVsLoaded,
}) {
  const selectedPeriodId = period?.fiscal_period_id ?? null;

  // ── Period slices ──────────────────────────────────────────────────────────
  const periodPlanInputs = (allPlanInputs ?? []).filter(
    (i) => i.fiscal_period_id === selectedPeriodId && i.is_active
  );
  const approvedInput   = periodPlanInputs.find((i) => i.approval_status === "approved") ?? null;
  const vcBudget        = approvedInput ? Number(approvedInput.variable_comp_budget) : null;
  const revenueTarget   = approvedInput ? Number(approvedInput.revenue_target)       : null;
  const overAssignRatio = approvedInput ? Number(approvedInput.over_assignment_ratio): null;

  const alignmentRow   = (allAlignmentData ?? []).find((r) => r.fiscal_period_id === selectedPeriodId);
  const totalCredits   = alignmentRow?.total_credits_reporting != null
    ? Number(alignmentRow.total_credits_reporting) : null;
  // S1038 OI-S1037-EST-REVENUE-FORMULA fix: Est. Revenue = total credits booked.
  // over_assignment_ratio inflates quota above revenue target — it does not
  // deflate revenue. Dividing by it was semantically wrong.
  const estRevenue     = totalCredits;

  // S1039 — Deployed Quota (from payout_summary via quota_by_period CTE).
  // Must be declared before ratio derivations below.
  const deployedQuota       = alignmentRow?.total_quota_reporting != null
    ? Number(alignmentRow.total_quota_reporting) : null;

  // S1039 ratio cards:
  //   quotaToRevRatio    — practical over-assignment multiplier (Quota / Rev Target, e.g. 1.11x).
  //   creditsToQuotaRatio — rep execution signal (Credits / Deployed Quota).
  //     >100%: reps produced more than quota — strong execution (or easy quota).
  //     <100%: reps fell short of quota — may signal discounting, pipeline issues.
  //   estAttainPct removed — credits are pre-contra; Credits / Rev Target is misleading.
  //   creditVsQuotaPct removed — superseded by creditsToQuotaRatio.
  const quotaToRevRatio     = (deployedQuota != null && revenueTarget != null && revenueTarget > 0)
    ? deployedQuota / revenueTarget : null;
  const creditsToQuotaRatio = (estRevenue != null && deployedQuota != null && deployedQuota > 0)
    ? estRevenue / deployedQuota : null;

  const periodBatches  = (allBatches ?? []).filter((b) => b.fiscal_period_id === selectedPeriodId);
  const releasedBatch  = periodBatches.find((b) => b.batch_status === "released") ?? null;
  // S1038 OI-S1037-PROJECTED-PAYOUT-FALLBACK fix: fall back to calc run total
  // (calculated_payout_amount from payout_summary WHERE is_latest_final=TRUE)
  // when no released statement batch exists. Released batch takes precedence.
  const actualPayout   = releasedBatch
    ? Number(releasedBatch.total_payout_amount)
    : alignmentRow?.calculated_payout_amount != null
      ? Number(alignmentRow.calculated_payout_amount)
      : null;
  const payoutVsBudget = (actualPayout != null && vcBudget != null && vcBudget > 0)
    ? (actualPayout / vcBudget) * 100 : null;

  // ── Role breakdown table data ──────────────────────────────────────────────
  // Build role set from aop-inputs (canonical — always present if AOP loaded)
  const periodAopRows = (allAopData ?? []).filter((r) => r.fiscal_period_id === selectedPeriodId);

  // Index exposure/by-role and plan-vs-loaded by role_code for O(1) join
  const exposureByRole  = {};
  (allExposureByRole ?? []).forEach((r) => {
    if (r.role_code) exposureByRole[r.role_code] = r;
  });
  const planVsLoadedByRole = {};
  (allPlanVsLoaded ?? []).forEach((r) => {
    if (r.role_code) planVsLoadedByRole[r.role_code] = r;
  });

  // Unique role_codes for filter dropdown
  const allRoleCodes    = [...new Set(periodAopRows.map((r) => r.role_code).filter(Boolean))].sort();
  const allSegmentCodes = [...new Set(periodAopRows.map((r) => r.segment_code).filter(Boolean))].sort();
  const allBuCodes      = [...new Set(periodAopRows.map((r) => r.bu_code).filter(Boolean))].sort();

  const [filterRole, setFilterRole]       = useState("");
  const [filterSegment, setFilterSegment] = useState("");
  const [filterBu, setFilterBu]           = useState("");

  const filteredRows = periodAopRows.filter((r) => {
    if (filterRole    && r.role_code    !== filterRole)    return false;
    if (filterSegment && r.segment_code !== filterSegment) return false;
    if (filterBu      && r.bu_code      !== filterBu)      return false;
    return true;
  });

  // Totals for total row
  const totalAopVC       = filteredRows.reduce((s, r) => s + Number(r.total_planned_variable_comp ?? 0), 0);
  const totalActualPayout= filteredRows.reduce((s, r) => {
    const exp = exposureByRole[r.role_code];
    return s + (exp?.actual_total_payout != null ? Number(exp.actual_total_payout) : 0);
  }, 0);

  const selectCls = "rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2.5 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none";

  const hasData = approvedInput != null || periodAopRows.length > 0;

  return (
    <div className="space-y-4">

      {/* ── Headline strip — S1039 layout ──────────────────────────────────────
           Row 1 (Revenue): Revenue Target | Credits Produced (pre-contra gross)
           Row 2 (VC):      VC Target | Projected/Actual Payout | % of VC Target
           Row 3 (Ratios):  Deployed Quota + Quota/Rev Target ratio |
                            Credits/Deployed Quota ratio
           Attainment (Credits / Rev Target) removed S1039 — credits are pre-contra
           so comparing them to a net revenue target is misleading for finance.
      ── */}

      {/* Row 1 — Revenue */}
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Revenue Target */}
        <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3">
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Revenue Target</p>
          <p className="mt-1 text-tok-lg font-bold text-[rgb(var(--color-text))]">
            {revenueTarget != null ? fmt(revenueTarget) : "—"}
          </p>
        </div>

        {/* Credits Produced — renamed from Est. Revenue S1039.
            Credits are pre-contra gross amounts — not equivalent to recognised revenue.
            Est. label retained so finance knows this is derived, not from ERP. */}
        <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3">
          <p className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Credits Produced <EstPill />
          </p>
          <p className="mt-1 text-tok-lg font-bold text-[rgb(var(--color-text))]">
            {estRevenue != null ? fmt(estRevenue) : "—"}
          </p>
        </div>
      </div>

      {/* Row 2 — Variable Comp */}
      <div className="grid gap-3 sm:grid-cols-3">
        {/* VC Target */}
        <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3">
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">VC Target</p>
          <p className="mt-1 text-tok-lg font-bold text-[rgb(var(--color-text))]">
            {vcBudget != null ? fmt(vcBudget) : "—"}
          </p>
        </div>

        {/* Actual Payout / Projected Payout (Est.) */}
        <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3">
          <p className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            {releasedBatch ? "Actual Payout" : <><span>Projected Payout</span><EstPill /></>}
          </p>
          <p className="mt-1 text-tok-lg font-bold text-[rgb(var(--color-text))]">
            {actualPayout != null ? fmt(actualPayout) : "—"}
          </p>
        </div>

        {/* % of VC Target */}
        <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3">
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">% of VC Target</p>
          <p className={`mt-1 text-tok-lg font-bold ${
            payoutVsBudget == null ? "text-[rgb(var(--color-text-muted))]"
            : payoutVsBudget > 100 ? "text-[rgb(var(--color-danger))]"
            : "text-[rgb(var(--color-success))]"
          }`}>
            {payoutVsBudget != null ? `${payoutVsBudget.toFixed(1)}%` : "—"}
          </p>
        </div>
      </div>

      {/* Row 3 — Quota / Credit ratios */}
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Deployed Quota + Quota / Revenue Target ratio */}
        <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3">
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Deployed Quota</p>
          <p className="mt-1 text-tok-lg font-bold text-[rgb(var(--color-text))]">
            {deployedQuota != null ? fmt(deployedQuota) : "—"}
          </p>
          {/* Quota / Rev Target ratio — practical over-assignment multiplier */}
          {quotaToRevRatio != null && (
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Quota / Rev Target:{" "}
              <span className="font-semibold text-[rgb(var(--color-text))]">
                {quotaToRevRatio.toFixed(2)}×
              </span>
            </p>
          )}
        </div>

        {/* Credits / Deployed Quota ratio — rep execution signal */}
        <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3">
          <p className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Credits / Deployed Quota <EstPill />
          </p>
          <p className={`mt-1 text-tok-lg font-bold ${
            creditsToQuotaRatio == null ? "text-[rgb(var(--color-text-muted))]"
            : creditsToQuotaRatio >= 1  ? "text-[rgb(var(--color-success))]"
            : creditsToQuotaRatio >= 0.9 ? "text-[rgb(var(--color-warning))]"
            : "text-[rgb(var(--color-danger))]"
          }`}>
            {creditsToQuotaRatio != null ? `${(creditsToQuotaRatio * 100).toFixed(2)}%` : "—"}
          </p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Signals rep execution vs quota set. Deviation may indicate discounting or pipeline timing.
          </p>
        </div>
      </div>

      {/* ── Role breakdown table ── */}
      <Card title="Role Breakdown">
        {!hasData && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No data for {period?.period_label ?? period?.period_code ?? "this period"}.
            Load AOP inputs and approve a Finance Plan Input to populate this view.
          </p>
        )}

        {hasData && (
          <>
            {/* Client-side filters */}
            {periodAopRows.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className={selectCls}>
                  <option value="">All Roles</option>
                  {allRoleCodes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filterSegment} onChange={(e) => setFilterSegment(e.target.value)} className={selectCls}>
                  <option value="">All Segments</option>
                  {allSegmentCodes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filterBu} onChange={(e) => setFilterBu(e.target.value)} className={selectCls}>
                  <option value="">All BUs</option>
                  {allBuCodes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {(filterRole || filterSegment || filterBu) && (
                  <button
                    onClick={() => { setFilterRole(""); setFilterSegment(""); setFilterBu(""); }}
                    className="rounded-xl border border-[rgb(var(--color-border))] px-2.5 py-1.5 text-tok-xs text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {filteredRows.length === 0 && periodAopRows.length > 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No rows match the selected filters.</p>
            )}

            {filteredRows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-tok-sm">
                  <thead>
                    <tr className="border-b border-[rgb(var(--color-border))]">
                      {["Role", "HC Plan", "HC Actual", "AOP Plan VC", "Actual Payout", "% of AOP", "Quota Loaded", "vs Plan"].map((h) => (
                        <th key={h} className="pb-2 pr-4 text-left text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))] last:pr-0">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const exp = exposureByRole[row.role_code]  ?? null;
                      const pvl = planVsLoadedByRole[row.role_code] ?? null;
                      const aopVC      = Number(row.total_planned_variable_comp ?? 0);
                      const actPayout  = exp?.actual_total_payout  != null ? Number(exp.actual_total_payout)  : null;
                      const hcActual   = exp?.position_count        != null ? Number(exp.position_count)        : null;
                      const quotaLoaded= pvl?.loaded_effective_quota != null ? Number(pvl.loaded_effective_quota): null;
                      const vsPlanPct  = pvl?.variance_pct           != null ? Number(pvl.variance_pct)          : null;
                      const pctOfAop   = (actPayout != null && aopVC > 0) ? (actPayout / aopVC) * 100 : null;

                      return (
                        <tr key={`${row.role_code}-${row.segment_code}-${row.bu_code}`}
                          className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className="py-2.5 pr-4 font-medium text-[rgb(var(--color-text))]">{row.role_code ?? "—"}</td>
                          <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text-muted))]">{row.total_planned_headcount ?? "—"}</td>
                          <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text-muted))]">{hcActual ?? "—"}</td>
                          <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text))]">{fmt(aopVC)}</td>
                          <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text))]">{actPayout != null ? fmt(actPayout) : "—"}</td>
                          <td className={`py-2.5 pr-4 tabular-nums font-medium ${
                            pctOfAop == null ? "text-[rgb(var(--color-text-muted))]"
                            : pctOfAop > 110 ? "text-[rgb(var(--color-danger))]"
                            : pctOfAop >= 90 ? "text-[rgb(var(--color-success))]"
                            : "text-[rgb(var(--color-warning))]"
                          }`}>
                            {pctOfAop != null ? `${pctOfAop.toFixed(1)}%` : "—"}
                          </td>
                          <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text-muted))]">{quotaLoaded != null ? fmt(quotaLoaded) : "—"}</td>
                          <td className={`py-2.5 tabular-nums font-medium ${
                            vsPlanPct == null ? "text-[rgb(var(--color-text-muted))]"
                            : Math.abs(vsPlanPct) < 5 ? "text-[rgb(var(--color-success))]"
                            : "text-[rgb(var(--color-warning))]"
                          }`}>
                            {vsPlanPct != null ? `${vsPlanPct > 0 ? "+" : ""}${vsPlanPct.toFixed(1)}%` : "—"}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Total row — VC Budget at total row ONLY (Option B, S607) */}
                    <tr className="bg-[rgb(var(--color-surface-muted))] font-semibold border-t-2 border-[rgb(var(--color-border))]">
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text))]">Total</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">—</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">—</td>
                      <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text))]">{fmt(totalAopVC)}</td>
                      <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text))]">{totalActualPayout > 0 ? fmt(totalActualPayout) : "—"}</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">—</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">—</td>
                      {/* vs Budget — Finance Budget (variable_comp_budget) at total row only */}
                      <td className={`py-2.5 tabular-nums font-semibold ${
                        payoutVsBudget == null ? "text-[rgb(var(--color-text-muted))]"
                        : payoutVsBudget > 100 ? "text-[rgb(var(--color-danger))]"
                        : "text-[rgb(var(--color-success))]"
                      }`}>
                        {payoutVsBudget != null
                          ? <>Actual vs Budget: {payoutVsBudget.toFixed(1)}%</>
                          : vcBudget != null
                            ? <>VC Target: {fmt(vcBudget)}</>
                            : "—"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Est. note — always visible, non-dismissable */}
            {/* S1039: removed stale "÷ over-assignment ratio" text (OI-S1039-FINANCE-DASHBOARD-EST-REVENUE-NOTE-STALE). */}
            {/* S1039: updated to reflect Deployed Quota card addition. */}
            <div className="mt-3 rounded-lg bg-[rgb(var(--color-warning))]/8 border border-[rgb(var(--color-warning))]/20 px-2.5 py-2 text-[10px] leading-4 text-[rgb(var(--color-text-muted))]">
              <span className="font-semibold text-[rgb(var(--color-warning))]">Note: </span>
              Est. Revenue = total commissionable credits converted to reporting currency.
              Deployed Quota = total quota deployed across all positions for the period.
              Covers commissionable activity only. Not a substitute for recognised revenue from your financial planning system.
            </div>
          </>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page: PlanGovernance — /finance/governance
//
// S608 Step 3. Three stacked sections — all existing components, no new data.
//   1. Finance Budget Commitment — PlanInputs (existing)
//   2. AOP Commitment Trail      — PlanVersions (existing)
//   3. Quota Deployment Check    — PlanVsLoadedPanel extracted from ExposureMonitor
//      (rendered here inline via PlanVsLoadedInline until Step 5 extracts it)
// ---------------------------------------------------------------------------

function PlanGovernance({ period, fiscalPeriodId }) {
  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Finance Budget Commitment</h2>
        <PlanInputs />
      </section>
      <section>
        <h2 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">AOP Commitment Trail</h2>
        <PlanVersions />
      </section>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page: PeriodClose — /finance/close
//
// S608 Step 4. Four stacked sections — all existing components, no new data.
//   1. Calculation Runs  — CalculationRuns (existing)
//   2. Statement Batches — StatementReview (existing)
//   3. Payroll           — PayrollReview + Reconciliation stacked (existing)
//   4. Disputes          — DisputesAggregate (existing)
// ---------------------------------------------------------------------------

function PeriodClose({ fiscalPeriodId }) {
  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Calculation Runs</h2>
        <CalculationRuns />
      </section>
      <section>
        <h2 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Statement Batches</h2>
        <StatementReview />
      </section>
      <section>
        <h2 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Payroll</h2>
        <PayrollReview />
        <div className="mt-4">
          <Reconciliation />
        </div>
      </section>
      <section>
        <h2 className="mb-3 text-tok-sm font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Disputes</h2>
        <DisputesAggregate />
      </section>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Panel: Financial Alignment
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// useLineChart — Canvas line chart hook
// Draws two labelled lines on a canvas ref.
// No third-party dependency — consistent with ExposureMonitor.jsx canvas pattern.
// series: [{ label, values: [number|null], color, dashed }]
// labels: string[] — x-axis period labels
// yFmt: function — formats y-axis tick values
// ---------------------------------------------------------------------------

function useLineChart(canvasRef, { series, labels, yFmt }) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr    = window.devicePixelRatio || 1;
    const cssW   = canvas.offsetWidth  || 400;
    const cssH   = canvas.offsetHeight || 180;
    canvas.width        = cssW * dpr;
    canvas.height       = cssH * dpr;
    canvas.style.width  = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssW, cssH);

    const PAD = { top: 16, right: 16, bottom: 36, left: 60 };
    const W   = cssW - PAD.left - PAD.right;
    const H   = cssH - PAD.top  - PAD.bottom;

    // Gather all non-null values to compute y-range
    const allVals = series.flatMap(s => s.values.filter(v => v != null));
    if (allVals.length === 0) return;

    const rawMin = Math.min(...allVals);
    const rawMax = Math.max(...allVals);
    const pad    = (rawMax - rawMin) * 0.15 || rawMax * 0.1 || 1;
    const yMin   = Math.max(0, rawMin - pad);
    const yMax   = rawMax + pad;

    const xStep = labels.length > 1 ? W / (labels.length - 1) : W;

    function xPos(i)  { return PAD.left + (labels.length > 1 ? i * xStep : W / 2); }
    function yPos(v)  { return PAD.top + H - ((v - yMin) / (yMax - yMin)) * H; }

    // Resolve CSS variables for grid / text colours
    const style     = getComputedStyle(canvas);
    const colBorder = `rgb(${style.getPropertyValue("--color-border").trim()})`;
    const colMuted  = `rgb(${style.getPropertyValue("--color-text-muted").trim()})`;

    // Grid lines (4 horizontal)
    ctx.strokeStyle = colBorder;
    ctx.lineWidth   = 0.5;
    for (let i = 0; i <= 4; i++) {
      const y = PAD.top + (H / 4) * i;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(PAD.left + W, y); ctx.stroke();
      const tickVal = yMax - ((yMax - yMin) / 4) * i;
      ctx.fillStyle   = colMuted;
      ctx.font        = `10px sans-serif`;
      ctx.textAlign   = "right";
      ctx.fillText(yFmt(tickVal), PAD.left - 6, y + 3.5);
    }

    // X-axis labels
    ctx.fillStyle = colMuted;
    ctx.font      = "10px sans-serif";
    ctx.textAlign = "center";
    labels.forEach((lbl, i) => {
      ctx.fillText(lbl, xPos(i), cssH - PAD.bottom + 14);
    });

    // Draw each series
    series.forEach(({ values, color, dashed }) => {
      ctx.strokeStyle = color;
      ctx.lineWidth   = 2;
      ctx.setLineDash(dashed ? [5, 4] : []);
      ctx.beginPath();
      let started = false;
      values.forEach((v, i) => {
        if (v == null) { started = false; return; }
        const x = xPos(i);
        const y = yPos(v);
        if (!started) { ctx.moveTo(x, y); started = true; }
        else          { ctx.lineTo(x, y); }
      });
      ctx.stroke();
      ctx.setLineDash([]);

      // Data points
      values.forEach((v, i) => {
        if (v == null) return;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(xPos(i), yPos(v), 3.5, 0, Math.PI * 2);
        ctx.fill();
      });
    });
  });
}

// ---------------------------------------------------------------------------
// AlignmentLineChart — thin wrapper rendering canvas + legend
// ---------------------------------------------------------------------------

function AlignmentLineChart({ title, series, labels, yFmt, estNote }) {
  const canvasRef = useRef(null);
  useLineChart(canvasRef, { series, labels, yFmt });

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] p-3">
      <div className="mb-1 flex items-center gap-2">
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">{title}</p>
        {estNote && (
          <span className="rounded px-1 py-0.5 text-[10px] font-semibold bg-[rgb(var(--color-warning))]/15 text-[rgb(var(--color-warning))]">Est.</span>
        )}
      </div>
      <canvas ref={canvasRef} className="w-full" style={{ height: 160 }} />
      <div className="mt-2 flex flex-wrap gap-3">
        {series.map((s) => (
          <span key={s.label} className="flex items-center gap-1.5 text-[10px] text-[rgb(var(--color-text-muted))]">
            <span
              className="inline-block h-0.5 w-5 rounded"
              style={{
                background: s.color,
                borderTop: s.dashed ? `2px dashed ${s.color}` : undefined,
                background: s.dashed ? "transparent" : s.color,
              }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// fmtAbbrev — abbreviated currency for chart y-axis (e.g. $1.6M, $920K)
// ---------------------------------------------------------------------------

function fmtAbbrev(v) {
  if (v == null || isNaN(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

// ---------------------------------------------------------------------------
// EstPill — mandatory amber Est. badge, non-dismissable
// ---------------------------------------------------------------------------

function EstPill() {
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide bg-[rgb(var(--color-warning))]/15 text-[rgb(var(--color-warning))] border border-[rgb(var(--color-warning))]/30">
      Est.
    </span>
  );
}

// ---------------------------------------------------------------------------
// FinancialAlignment — S607 redesign
//
// Period state lifted to shell — received as props (mirrors CompAdmin S602).
// Props: period, periods, allBatches, allPlanInputs, allAopInputs, allAlignmentData
//
// Three comparison blocks (top half):
//   1. Revenue — Est. Revenue vs Revenue Target, Attainment (Est.), VC% delta
//   2. Variable Comp — Actual Total Payout vs VC Budget, Payout %
//   3. Headcount — Actual HC vs Planned HC, delta
//
// Three Canvas line charts (bottom half):
//   1. Est. Revenue vs Revenue Target (dashed est. line)
//   2. Actual Payout vs VC Budget
//   3. Actual HC vs Planned HC
//
// Est. Revenue = total_credits_reporting (FX-converted to reporting currency via
//   exchange_rate JOIN in financial_alignment_router.py credits_by_period CTE).
//   S1038 OI-S1037-EST-REVENUE-FORMULA: over_assignment_ratio removed from formula —
//   it inflates quota above revenue target, it does not deflate revenue.
//   Sourced from financial-alignment endpoint field total_credits_reporting.
//   Graceful stub when field absent (OI-FINANCE-CREDITS-AGGREGATE-ENDPOINT).
//
// statement-batches: fetched without fiscal_period_id filter (fix for
//   OI-FINANCE-ALIGNMENT-ACTUAL-NOT-SHOWING — filter was silently failing or
//   selecting wrong period due to stale default). Filtered client-side by
//   fiscal_period_id, consistent with StatementReview pattern.
// ---------------------------------------------------------------------------

function FinancialAlignment({ period, periods, allBatches, allPlanInputs, allAopData, allAlignmentData }) {
  const selectedPeriodId = period?.fiscal_period_id ?? null;

  // ── Per-period slices ──────────────────────────────────────────────────────

  // financial-plan-inputs: filter to selected period, active, approved
  const periodPlanInputs  = (allPlanInputs ?? []).filter(
    i => i.fiscal_period_id === selectedPeriodId && i.is_active
  );
  const approvedInput     = periodPlanInputs.find(i => i.approval_status === "approved") ?? null;
  const vcBudget          = approvedInput ? Number(approvedInput.variable_comp_budget) : null;
  const revenueTarget     = approvedInput ? Number(approvedInput.revenue_target)        : null;
  const overAssignRatio   = approvedInput ? Number(approvedInput.over_assignment_ratio) : null;

  // aop-inputs: filter to selected period, sum headcount
  const periodAopRows     = (allAopData ?? []).filter(r => r.fiscal_period_id === selectedPeriodId);
  const aopPlannedHC      = periodAopRows.reduce((s, r) => s + Number(r.planned_headcount ?? r.total_planned_headcount ?? 0), 0);
  const aopPlannedVC      = periodAopRows.reduce((s, r) => s + Number(r.planned_variable_comp ?? r.total_planned_variable_comp ?? 0), 0);
  const aopLoaded         = periodAopRows.length > 0;

  // statement-batches: client-side filter by fiscal_period_id (fixes OI-FINANCE-ALIGNMENT-ACTUAL-NOT-SHOWING)
  // StatementReview fetches without filter and works — same pattern used here.
  const periodBatches     = (allBatches ?? []).filter(b => b.fiscal_period_id === selectedPeriodId);
  const releasedBatch     = periodBatches.find(b => b.batch_status === "released") ?? null;

  // financial-alignment endpoint: total_credits_reporting (OI-FINANCE-CREDITS-AGGREGATE-ENDPOINT)
  // Stub gracefully if field absent — frontend ships independently of backend extension.
  const alignmentRow      = (allAlignmentData ?? []).find(r => r.fiscal_period_id === selectedPeriodId);
  const totalCredits      = alignmentRow?.total_credits_reporting != null
    ? Number(alignmentRow.total_credits_reporting)
    : null;

  // population-breakdown: actual HC — sourced from allAlignmentData which includes position_count
  // If not yet available from alignment endpoint, fall back to null (stub state)
  const actualHC          = alignmentRow?.active_position_count ?? null;

  // S1038 OI-S1037-PROJECTED-PAYOUT-FALLBACK fix: fall back to calc run total
  // (calculated_payout_amount from payout_summary WHERE is_latest_final=TRUE)
  // when no released statement batch exists. Released batch takes precedence.
  const actualPayout      = releasedBatch
    ? Number(releasedBatch.total_payout_amount)
    : alignmentRow?.calculated_payout_amount != null
      ? Number(alignmentRow.calculated_payout_amount)
      : null;

  // ── Derived metrics ────────────────────────────────────────────────────────

  // S1038 OI-S1037-EST-REVENUE-FORMULA fix: Est. Revenue = total credits booked.
  // over_assignment_ratio inflates quota above revenue target — does not deflate revenue.
  const estRevenue = totalCredits;

  // Revenue attainment (Est.) = estRevenue ÷ revenueTarget × 100
  const estAttainmentPct = (estRevenue != null && revenueTarget != null && revenueTarget > 0)
    ? (estRevenue / revenueTarget) * 100
    : null;

  // VC% of Est. Revenue = actualPayout ÷ estRevenue × 100
  const vcPctEstRev = (actualPayout != null && estRevenue != null && estRevenue > 0)
    ? (actualPayout / estRevenue) * 100
    : null;

  // VC Budget% of Revenue Target = vcBudget ÷ revenueTarget × 100
  const vcBudgetPctRevTgt = (vcBudget != null && revenueTarget != null && revenueTarget > 0)
    ? (vcBudget / revenueTarget) * 100
    : null;

  // Delta (ppt) = vcPctEstRev − vcBudgetPctRevTgt  (positive = comp running hotter than plan as % of revenue)
  const vcPctDeltaPpt = (vcPctEstRev != null && vcBudgetPctRevTgt != null)
    ? vcPctEstRev - vcBudgetPctRevTgt
    : null;

  // Payout vs VC Budget %
  const payoutVsBudgetPct = (actualPayout != null && vcBudget != null && vcBudget > 0)
    ? (actualPayout / vcBudget) * 100
    : null;

  // HC delta
  const hcDelta = (actualHC != null && aopPlannedHC > 0) ? actualHC - aopPlannedHC : null;

  // ── Multi-period series for charts ─────────────────────────────────────────
  // Use all periods sorted ascending (earliest left) for chart x-axis.
  const chartPeriods = [...(periods ?? [])]
    .sort((a, b) => a.fiscal_period_id - b.fiscal_period_id);

  const chartLabels = chartPeriods.map(p => {
    const lbl = p.period_label ?? p.period_code ?? String(p.fiscal_period_id);
    // Abbreviate: "FY2026-M04" → "M04"
    return lbl.replace(/^FY\d{4}-/, "");
  });

  function getApproved(pid) {
    return (allPlanInputs ?? []).filter(i => i.fiscal_period_id === pid && i.is_active)
      .find(i => i.approval_status === "approved") ?? null;
  }
  function getReleasedBatch(pid) {
    return (allBatches ?? []).filter(b => b.fiscal_period_id === pid)
      .find(b => b.batch_status === "released") ?? null;
  }
  function getAopHC(pid) {
    const rows = (allAopData ?? []).filter(r => r.fiscal_period_id === pid);
    return rows.length > 0
      ? rows.reduce((s, r) => s + Number(r.planned_headcount ?? r.total_planned_headcount ?? 0), 0)
      : null;
  }
  function getAlignment(pid) {
    return (allAlignmentData ?? []).find(r => r.fiscal_period_id === pid);
  }
  function getEstRev(pid) {
    const inp  = getApproved(pid);
    const aln  = getAlignment(pid);
    const cred = aln?.total_credits_reporting != null ? Number(aln.total_credits_reporting) : null;
    const rat  = inp ? Number(inp.over_assignment_ratio) : null;
    return (cred != null && rat != null && rat > 0) ? cred / rat : null;
  }

  const CSS_PRIMARY = `rgb(var(--color-primary))`;
  const CSS_SUCCESS = `rgb(var(--color-success))`;
  const CSS_WARNING = `rgb(var(--color-warning))`;

  // Resolve CSS vars to actual hex at render — canvas needs concrete colours
  // CSS vars resolve via getComputedStyle on the document element
  const resolveColor = (cssVar) => {
    if (typeof document === "undefined") return "#6366f1";
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue(cssVar.replace("rgb(var(", "").replace("))", "").trim())
      .trim();
    return val ? `rgb(${val})` : "#6366f1";
  };

  const colPrimary = resolveColor("--color-primary");
  const colSuccess = resolveColor("--color-success");
  const colWarning = resolveColor("--color-warning");
  const colDanger  = resolveColor("--color-danger");

  const chartRevSeries = [
    {
      label: "Revenue Target",
      values: chartPeriods.map(p => { const i = getApproved(p.fiscal_period_id); return i ? Number(i.revenue_target) : null; }),
      color: colPrimary, dashed: false,
    },
    {
      label: "Est. Revenue",
      values: chartPeriods.map(p => getEstRev(p.fiscal_period_id)),
      color: colWarning, dashed: true,
    },
  ];

  const chartVCSeries = [
    {
      label: "VC Budget",
      values: chartPeriods.map(p => { const i = getApproved(p.fiscal_period_id); return i ? Number(i.variable_comp_budget) : null; }),
      color: colPrimary, dashed: false,
    },
    {
      label: "Actual Payout",
      values: chartPeriods.map(p => { const b = getReleasedBatch(p.fiscal_period_id); return b ? Number(b.total_payout_amount) : null; }),
      color: colSuccess, dashed: false,
    },
  ];

  const chartHCSeries = [
    {
      label: "Planned HC",
      values: chartPeriods.map(p => getAopHC(p.fiscal_period_id)),
      color: colPrimary, dashed: false,
    },
    {
      label: "Actual HC",
      values: chartPeriods.map(p => getAlignment(p.fiscal_period_id)?.active_position_count ?? null),
      color: colSuccess, dashed: false,
    },
  ];

  // ── Helpers ────────────────────────────────────────────────────────────────

  function fmtPct(v, dp = 1) {
    if (v == null) return "—";
    return `${v.toFixed(dp)}%`;
  }

  function deltaColor(v, positiveIsBad = false) {
    if (v == null) return "text-[rgb(var(--color-text-muted))]";
    if (v === 0)   return "text-[rgb(var(--color-text-muted))]";
    const over = v > 0;
    return (over === positiveIsBad)
      ? "text-[rgb(var(--color-danger))]"
      : "text-[rgb(var(--color-success))]";
  }

  function fmtDelta(v, prefix = "", suffix = "", positiveIsBad = false) {
    if (v == null) return <span className="text-[rgb(var(--color-text-muted))]">—</span>;
    const sign  = v > 0 ? "+" : "";
    const cls   = `tabular-nums font-semibold ${deltaColor(v, positiveIsBad)}`;
    return <span className={cls}>{sign}{prefix}{Math.abs(v).toFixed(1)}{suffix}</span>;
  }

  // ── Metric row helper ──────────────────────────────────────────────────────

  function MetricRow({ label, value, sub, estTag }) {
    return (
      <div className="flex items-start justify-between py-1.5 border-b border-[rgb(var(--color-border))] last:border-0">
        <span className="text-tok-xs text-[rgb(var(--color-text-muted))] flex items-center gap-1">
          {label}{estTag && <EstPill />}
        </span>
        <span className="text-tok-sm font-semibold tabular-nums text-[rgb(var(--color-text))] text-right">
          {value}
          {sub && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">{sub}</span>}
        </span>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const hasAnyData = vcBudget != null || actualPayout != null || revenueTarget != null;

  return (
    <div className="space-y-4">
      <Card title="Financial Alignment">

        {/* ── TOP HALF: three comparison blocks ── */}
        <div className="grid gap-3 sm:grid-cols-3">

          {/* Block 1 — Revenue */}
          <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-0.5">
            <p className="mb-2 text-tok-xs font-bold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Revenue</p>

            <MetricRow
              label="Revenue Target"
              value={revenueTarget != null ? fmt(revenueTarget) : "—"}
            />
            <MetricRow
              label="Est. Revenue"
              estTag
              value={estRevenue != null
                ? <span className="flex items-center gap-1">{fmt(estRevenue)}</span>
                : overAssignRatio == null
                  ? <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Ratio not set</span>
                  : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Credits pending</span>
              }
            />
            <MetricRow
              label="Attainment"
              estTag
              value={estAttainmentPct != null
                ? <span className={deltaColor(estAttainmentPct - 100, false)}>
                    {fmtPct(estAttainmentPct)}
                  </span>
                : "—"
              }
            />
            <div className="mt-2 border-t border-[rgb(var(--color-border))] pt-2 space-y-0.5">
              <MetricRow
                label="VC% of Est. Revenue"
                estTag
                value={fmtPct(vcPctEstRev)}
              />
              <MetricRow
                label="VC Budget % of Rev. Target"
                value={fmtPct(vcBudgetPctRevTgt)}
              />
              <div className="flex items-start justify-between py-1.5">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))] flex items-center gap-1">
                  Delta <EstPill />
                </span>
                <span className="text-tok-sm font-semibold tabular-nums">
                  {fmtDelta(vcPctDeltaPpt, "", " ppt", true)}
                </span>
              </div>
            </div>

            {/* Mandatory Est. note — never omitted */}
            <div className="mt-2 rounded-lg bg-[rgb(var(--color-warning))]/8 border border-[rgb(var(--color-warning))]/20 px-2.5 py-2 text-[10px] leading-4 text-[rgb(var(--color-text-muted))]">
              <span className="font-semibold text-[rgb(var(--color-warning))]">Note: </span>
              Est. Revenue = total commissionable credits (reporting currency) ÷ over-assignment ratio
              {overAssignRatio != null ? ` (${overAssignRatio.toFixed(4)})` : ""}.
              Covers commissionable activity only. Not a substitute for recognised revenue from your financial planning system.
            </div>
          </div>

          {/* Block 2 — Variable Comp */}
          <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-0.5">
            <p className="mb-2 text-tok-xs font-bold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Variable Comp</p>

            <MetricRow
              label="Variable Comp Budget"
              value={vcBudget != null ? fmt(vcBudget) : "—"}
              sub={approvedInput ? `v${approvedInput.version ?? 1}` : undefined}
            />
            <MetricRow
              label="Actual Total Payout"
              value={actualPayout != null
                ? fmt(actualPayout)
                : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                    {releasedBatch === null && periodBatches.length > 0 ? "Not released" : "No batch"}
                  </span>
              }
              sub={releasedBatch ? `${releasedBatch.statement_count} stmts` : undefined}
            />

            {payoutVsBudgetPct != null && (
              <div className="flex items-start justify-between py-1.5 border-b border-[rgb(var(--color-border))]">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Payout vs Budget</span>
                <span className={`text-tok-sm font-semibold tabular-nums ${payoutVsBudgetPct > 100 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
                  {fmtPct(payoutVsBudgetPct)}
                  <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
                    {payoutVsBudgetPct <= 100 ? "within budget ✓" : "over — review"}
                  </span>
                </span>
              </div>
            )}

            <MetricRow
              label="AOP Planned VC"
              value={aopLoaded ? fmt(aopPlannedVC) : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Not loaded</span>}
              sub={aopLoaded ? `${periodAopRows.length} roles` : undefined}
            />

            {actualPayout != null && aopLoaded && (
              <div className="flex items-start justify-between py-1.5">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Actual vs AOP</span>
                <span className="text-tok-sm">
                  {fmtDelta(actualPayout - aopPlannedVC, "$", "", true)}
                </span>
              </div>
            )}
          </div>

          {/* Block 3 — Headcount */}
          <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-0.5">
            <p className="mb-2 text-tok-xs font-bold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Headcount</p>

            <MetricRow
              label="Planned HC (AOP)"
              value={aopLoaded ? aopPlannedHC : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Not loaded</span>}
            />
            <MetricRow
              label="Actual HC"
              value={actualHC != null
                ? actualHC
                : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Pending</span>
              }
            />

            {hcDelta != null && (
              <div className="flex items-start justify-between py-1.5">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Delta</span>
                <span className={`text-tok-sm font-semibold tabular-nums ${
                  hcDelta === 0 ? "text-[rgb(var(--color-success))]"
                  : hcDelta > 0 ? "text-[rgb(var(--color-warning))]"  // over-hired
                  : "text-[rgb(var(--color-danger))]"                  // under-hired
                }`}>
                  {hcDelta > 0 ? "+" : ""}{hcDelta}
                  <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
                    {hcDelta === 0 ? "on plan" : hcDelta > 0 ? "over plan" : "under plan"}
                  </span>
                </span>
              </div>
            )}

            {/* Avg VC per head — both hard numbers, no Est. needed */}
            {actualPayout != null && actualHC != null && actualHC > 0 && (
              <div className="mt-2 border-t border-[rgb(var(--color-border))] pt-2">
                <MetricRow
                  label="Actual VC / Head"
                  value={fmt(actualPayout / actualHC)}
                />
                {vcBudget != null && (
                  <MetricRow
                    label="Budgeted VC / Head"
                    value={aopPlannedHC > 0 ? fmt(vcBudget / aopPlannedHC) : "—"}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* No data state */}
        {!hasAnyData && (
          <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            No alignment data for {period?.period_label ?? period?.period_code ?? "this period"}.
            Load AOP inputs and create an approved Finance plan input to begin.
          </p>
        )}

        {/* ── BOTTOM HALF: three line charts ── */}
        {chartPeriods.length > 0 && (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <AlignmentLineChart
              title="Est. Revenue vs Revenue Target"
              series={chartRevSeries}
              labels={chartLabels}
              yFmt={fmtAbbrev}
              estNote
            />
            <AlignmentLineChart
              title="Actual Payout vs VC Budget"
              series={chartVCSeries}
              labels={chartLabels}
              yFmt={fmtAbbrev}
            />
            <AlignmentLineChart
              title="Actual HC vs Planned HC"
              series={chartHCSeries}
              labels={chartLabels}
              yFmt={(v) => Math.round(v).toString()}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: Plan Inputs (OI-S394-04)
//
// Flow: finance creates (draft) → different finance user approves (approved).
// NN-36: approved_by ≠ created_by — enforced server-side at approve step.
// No /submit endpoint exists in backend — flow is draft → approved directly.
//
// Create:  POST /api/v1/financial-plan-inputs                        §17.1
// Approve: POST /api/v1/financial-plan-inputs/{id}/approve           §17.2
//          NN-36 enforced server-side — 403 SELF_APPROVAL_BLOCKED
//          if approver == creator.
//
// DD-04: no cross-shell imports. DD-05: role from JWT. DD-06: live API only.
// NN-COLUMNS: all field names verified against ddl_summary_r39.md.
// ---------------------------------------------------------------------------

function PlanInputs() {
  const { user } = useAuth();

  // Active open period — DD-06, no hardcoding
  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods?period_status=open`);
  const period = periodsData?.data?.[0] ?? null;

  // List state — managed manually for clean refresh without page reload
  const [inputs, setInputs]           = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]     = useState(null);

  // Create form
  const [showCreate, setShowCreate]   = useState(false);
  const [creating, setCreating]       = useState(false);
  const [createError, setCreateError] = useState(null);
  const EMPTY_FORM = {
    revenue_target:        "",
    variable_comp_budget:  "",
    budget_currency_code:  "USD",
    source_system:         "",
    effective_date:        "",
    over_assignment_ratio: "",
    source_reference_id:   "",
    revision_reason:       "",
  };
  const [createForm, setCreateForm] = useState(EMPTY_FORM);

  // Approve action
  const [approving, setApproving]   = useState(null); // holds id being approved
  const [approveError, setApproveError] = useState(null);

  function loadInputs() {
    setListLoading(true);
    setListError(null);
    fetch(`${API_BASE}/financial-plan-inputs`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setInputs(d?.data ?? []))
      .catch((e) => setListError(e.message))
      .finally(() => setListLoading(false));
  }

  useEffect(() => { loadInputs(); }, []);

  function setField(k, v) {
    setCreateForm((prev) => ({ ...prev, [k]: v }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!period) return;
    setCreating(true);
    setCreateError(null);
    try {
      const body = {
        fiscal_period_id:     period.fiscal_period_id,
        revenue_target:       createForm.revenue_target,
        variable_comp_budget: createForm.variable_comp_budget,
        budget_currency_code: createForm.budget_currency_code,
        source_system:        createForm.source_system,
        effective_date:       createForm.effective_date,
      };
      if (createForm.over_assignment_ratio)
        body.over_assignment_ratio = createForm.over_assignment_ratio;
      if (createForm.source_reference_id)
        body.source_reference_id = createForm.source_reference_id;
      if (createForm.revision_reason)
        body.revision_reason = createForm.revision_reason;

      const r = await fetch(`${API_BASE}/financial-plan-inputs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err?.error?.message ?? r.status);
      }
      setShowCreate(false);
      setCreateForm(EMPTY_FORM);
      loadInputs();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleApprove(id) {
    setApproving(id);
    setApproveError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-plan-inputs/${id}/approve`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err?.error?.message ?? r.status);
      }
      loadInputs();
    } catch (err) {
      setApproveError(err.message);
    } finally {
      setApproving(null);
    }
  }

  const inputCls = "w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]";
  const labelCls = "mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]";

  return (
    <div className="space-y-4">
      <Card
        title="Plan Inputs"
        action={
          !showCreate && (
            <Button variant="secondary" onClick={() => { setShowCreate(true); setCreateError(null); }}>
              + New Plan Input
            </Button>
          )
        }
      >
        {/* Context note */}
        <div className="mb-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Finance creates plan inputs for the period. A second Finance user approves (separation of duties).
          Payroll export is blocked until an approved plan input is on record.
        </div>

        {/* ── Create form ── */}
        {showCreate && (
          <form
            onSubmit={handleCreate}
            className="mb-4 space-y-4 rounded-xl border border-[rgb(var(--color-border))] p-4"
          >
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">New Plan Input</p>

            {/* Fiscal period — read-only from live API (DD-06) */}
            <div>
              <label className={labelCls}>Fiscal Period</label>
              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
                {period
                  ? `${period.period_label ?? period.period_code} (ID: ${period.fiscal_period_id})`
                  : "Loading period…"}
              </div>
            </div>

            {/* Required fields — row 1 */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={labelCls}>
                  Revenue Target <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  required
                  placeholder="e.g. 50000000.0000"
                  value={createForm.revenue_target}
                  onChange={(e) => setField("revenue_target", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>
                  Variable Comp Budget <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  required
                  placeholder="e.g. 5000000.0000"
                  value={createForm.variable_comp_budget}
                  onChange={(e) => setField("variable_comp_budget", e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Required fields — row 2 */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className={labelCls}>
                  Budget Currency <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  type="text"
                  required
                  maxLength={3}
                  placeholder="USD"
                  value={createForm.budget_currency_code}
                  onChange={(e) => setField("budget_currency_code", e.target.value.toUpperCase())}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>
                  Source System <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  placeholder="e.g. Adaptive Planning"
                  value={createForm.source_system}
                  onChange={(e) => setField("source_system", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>
                  Effective Date <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={createForm.effective_date}
                  onChange={(e) => setField("effective_date", e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Optional fields */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className={labelCls}>Over-Assignment Ratio</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  placeholder="e.g. 1.1000"
                  value={createForm.over_assignment_ratio}
                  onChange={(e) => setField("over_assignment_ratio", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Source Reference ID</label>
                <input
                  type="text"
                  maxLength={120}
                  placeholder="e.g. AOP-FY26-Q1"
                  value={createForm.source_reference_id}
                  onChange={(e) => setField("source_reference_id", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Revision Reason</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={createForm.revision_reason}
                  onChange={(e) => setField("revision_reason", e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            {createError && <ErrorNote message={createError} />}

            <div className="flex gap-2">
              <Button type="submit" disabled={creating || !period}>
                {creating ? "Saving…" : "Save as Draft"}
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() => { setShowCreate(false); setCreateForm(EMPTY_FORM); setCreateError(null); }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* ── List ── */}
        {listLoading && <LoadingRow />}
        {listError && <ErrorNote message="Unable to load plan inputs." />}
        {!listLoading && !listError && inputs.length === 0 && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No plan inputs for this period. Use "+ New Plan Input" to create one.
          </p>
        )}

        {inputs.map((item) => {
          // NN-36: block self-approval in UI — server also enforces (403 SELF_APPROVAL_BLOCKED)
          const isSelfCreated = user?.user_id && item.created_by &&
            String(item.created_by) === String(user.user_id);
          const canApprove = item.approval_status === "draft" && !isSelfCreated;

          return (
            <div
              key={item.financial_plan_input_id}
              className="mt-3 rounded-xl border border-[rgb(var(--color-border))] p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="grid gap-2 sm:grid-cols-4">
                  <Field label="Period" value={item.period_label ?? item.period_code ?? String(item.fiscal_period_id)} />  {/* OI-S209-06 */}
                  <Field label="Revenue Target" value={fmt(item.revenue_target, item.budget_currency_code)} />
                  <Field label="Variable Comp Budget" value={fmt(item.variable_comp_budget, item.budget_currency_code)} />
                  <div>
                    <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
                    <ApprovalBadge status={item.approval_status} />
                  </div>
                </div>

                {/* Approve action — draft only, NN-36 self-approval blocked */}
                {item.approval_status === "draft" && (
                  canApprove ? (
                    <Button
                      onClick={() => handleApprove(item.financial_plan_input_id)}
                      disabled={approving === item.financial_plan_input_id}
                    >
                      {approving === item.financial_plan_input_id ? "Approving…" : "Approve"}
                    </Button>
                  ) : (
                    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      Waiting for a second Finance user to approve (separation of duties)
                    </div>
                  )
                )}

                {/* Approved — locked state */}
                {item.approval_status === "approved" && (
                  <Pill tone="Complete">Approved</Pill>
                )}

                {/* Superseded */}
                {item.approval_status === "superseded" && (
                  <Pill tone="Not Ready">Superseded</Pill>
                )}
              </div>

              {/* Secondary metadata */}
              <div className="mt-2 grid gap-2 sm:grid-cols-3 border-t border-[rgb(var(--color-border))] pt-2">
                <Field label="Source System" value={item.source_system} />
                <Field label="Effective Date" value={item.effective_date ?? "—"} />
                <Field label="Version" value={item.version ?? "—"} />
              </div>
            </div>
          );
        })}

        {approveError && <ErrorNote message={approveError} />}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: Calculation Runs (read-only — finance cannot initiate runs)
// ---------------------------------------------------------------------------

function CalculationRuns() {
  const { data, loading, error } = useFetch(`${API_BASE}/calculation-runs`);
  const allRuns = data?.calculation_runs ?? [];

  const [search, setSearch]   = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortAsc, setSortAsc] = useState(false);

  function toggleSort(key) {
    if (sortKey === key) setSortAsc((v) => !v);
    else { setSortKey(key); setSortAsc(false); }
  }

  const runs = allRuns
    .filter((r) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (r.run_type ?? "").toLowerCase().includes(q) ||
        (r.run_status ?? "").toLowerCase().includes(q) ||
        String(r.fiscal_period_id).includes(q) ||
        (r.period_label ?? r.period_code ?? "").toLowerCase().includes(q) ||
        (r.calculation_run_id ?? "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      if (sortKey === "created_at") { av = new Date(av); bv = new Date(bv); }
      return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const SortTh = ({ label, k }) => (
    <th
      onClick={() => toggleSort(k)}
      className="cursor-pointer pb-2 pr-4 text-left text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))] select-none hover:text-[rgb(var(--color-text))]"
    >
      {label}{sortKey === k ? (sortAsc ? " ↑" : " ↓") : ""}
    </th>
  );

  return (
    <div className="space-y-4">
      <Card title={`Calculation Runs${allRuns.length ? ` (${allRuns.length})` : ""}`}>
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load calculation runs." />}
        {!loading && !error && (
          <>
            <div className="mb-3">
              <input
                type="search"
                placeholder="Search by type, status, period…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full max-w-xs rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
              />
            </div>
            {runs.length === 0 ? (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                {search ? "No runs match your search." : "No calculation runs found."}
              </p>
            ) : (
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-tok-sm">
                  <thead className="sticky top-0 bg-[rgb(var(--color-surface))]">
                    <tr className="border-b border-[rgb(var(--color-border))]">
                      <SortTh label="Run Type"  k="run_type" />
                      <SortTh label="Status"    k="run_status" />
                      <SortTh label="Period"    k="fiscal_period_id" />
                      <SortTh label="Created"   k="created_at" />
                      <th className="pb-2 pr-4 text-left text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Run ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => (
                      <tr key={run.calculation_run_id} className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className="py-2.5 pr-4">
                          <Pill tone={run.run_type === "final" ? "Complete" : "Draft"}>
                            {run.run_type}
                          </Pill>
                        </td>
                        <td className="py-2.5 pr-4">
                          <Pill tone={run.run_status === "completed" ? "Complete" : run.run_status === "failed" ? "Blocked" : "Warning"}>
                            {run.run_status}
                          </Pill>
                        </td>
                        <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">
                          {run.period_label ?? run.period_code ?? String(run.fiscal_period_id)}
                        </td>
                        <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">
                          {run.created_at ? new Date(run.created_at).toLocaleString() : "—"}
                        </td>
                        <td className="py-2.5 pr-4 font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {run.calculation_run_id?.slice(0, 8) ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: Statement Review
// ---------------------------------------------------------------------------

function StatementReview() {
  const { data, loading, error } = useFetch(`${API_BASE}/statement-batches`);
  const allBatches = data?.data ?? [];

  const [search, setSearch]   = useState("");
  const [sortKey, setSortKey] = useState("released_at");
  const [sortAsc, setSortAsc] = useState(false);

  function toggleSort(key) {
    if (sortKey === key) setSortAsc((v) => !v);
    else { setSortKey(key); setSortAsc(false); }
  }

  const batches = allBatches
    .filter((b) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (b.period_label ?? b.period_code ?? "").toLowerCase().includes(q) ||
        (b.batch_status ?? "").toLowerCase().includes(q) ||
        (b.statement_batch_id ?? "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      if (sortKey === "released_at" || sortKey === "created_at") {
        av = av ? new Date(av) : new Date(0);
        bv = bv ? new Date(bv) : new Date(0);
      }
      if (sortKey === "total_payout_amount" || sortKey === "statement_count") {
        av = Number(av); bv = Number(bv);
      }
      return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const SortTh = ({ label, k }) => (
    <th
      onClick={() => toggleSort(k)}
      className="cursor-pointer pb-2 pr-4 text-left text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))] select-none hover:text-[rgb(var(--color-text))]"
    >
      {label}{sortKey === k ? (sortAsc ? " ↑" : " ↓") : ""}
    </th>
  );

  // Summary totals
  const totalPayout = allBatches.reduce((s, b) => s + Number(b.total_payout_amount ?? 0), 0);
  const totalCount  = allBatches.reduce((s, b) => s + Number(b.statement_count ?? 0), 0);

  return (
    <div className="space-y-4">
      <Card title={`Statement Review${allBatches.length ? ` (${allBatches.length} batches)` : ""}`}>
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load statement batches." />}
        {!loading && !error && (
          <>
            {/* Summary totals */}
            {allBatches.length > 0 && (
              <div className="mb-3 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Total Batches</p>
                  <p className="text-tok-base font-bold text-[rgb(var(--color-text))]">{allBatches.length}</p>
                </div>
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Total Statements</p>
                  <p className="text-tok-base font-bold text-[rgb(var(--color-text))]">{totalCount}</p>
                </div>
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Total Payout</p>
                  <p className="text-tok-base font-bold text-[rgb(var(--color-text))]">{fmt(totalPayout)}</p>
                </div>
              </div>
            )}

            <div className="mb-3">
              <input
                type="search"
                placeholder="Search by period, status…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full max-w-xs rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
              />
            </div>

            {batches.length === 0 ? (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                {search ? "No batches match your search." : "No statement batches found."}
              </p>
            ) : (
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-tok-sm">
                  <thead className="sticky top-0 bg-[rgb(var(--color-surface))]">
                    <tr className="border-b border-[rgb(var(--color-border))]">
                      <SortTh label="Period"       k="fiscal_period_id" />
                      <SortTh label="Status"       k="batch_status" />
                      <SortTh label="Count"        k="statement_count" />
                      <SortTh label="Total Payout" k="total_payout_amount" />
                      <SortTh label="Released"     k="released_at" />
                      <th className="pb-2 text-left text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Batch ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((batch) => (
                      <tr key={batch.statement_batch_id} className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className="py-2.5 pr-4 text-[rgb(var(--color-text))]">
                          {batch.period_label ?? batch.period_code ?? `Period ${batch.fiscal_period_id}`}
                        </td>
                        <td className="py-2.5 pr-4">
                          <Pill tone={batch.batch_status === "released" ? "Complete" : batch.batch_status === "draft" ? "Draft" : "Warning"}>
                            {batch.batch_status}
                          </Pill>
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text-muted))]">
                          {batch.statement_count ?? "—"}
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums font-medium text-[rgb(var(--color-text))]">
                          {fmt(batch.total_payout_amount, batch.payout_currency_code)}
                        </td>
                        <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">
                          {batch.released_at ? new Date(batch.released_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-2.5 font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {batch.statement_batch_id?.slice(0, 8) ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: Payroll Review (read-only for finance — §16 is comp_admin only)
//
// Authorization status indicator derives from financial_plan_input.approval_status
// for the current open period via GET /api/v1/financial-plan-inputs.
// Finance has no access to payroll_export_batch endpoints (comp_admin only).
// S228 — §10.5 Finance payroll authorization gate.
// ---------------------------------------------------------------------------

function PayrollReview() {
  const { user } = useAuth();
  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods?period_status=open`);
  const period = periodsData?.data?.[0] ?? null;

  // Managed state for plan inputs — needed for optimistic refresh after approve
  const [inputs, setInputs]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [approving, setApproving] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [approveSuccess, setApproveSuccess] = useState(false);

  function loadInputs(periodId) {
    if (!periodId) return;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/financial-plan-inputs?fiscal_period_id=${periodId}`, {
      credentials: "include",
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setInputs(d?.data ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (period?.fiscal_period_id) loadInputs(period.fiscal_period_id);
  }, [period?.fiscal_period_id]);

  const approvedInput = inputs.find(
    (i) => i.approval_status === "approved" && i.is_active && !i.end_date
  );
  const isAuthorized = !!approvedInput;

  // Draft input that a different finance user can approve (NN-36)
  const approvableInput = inputs.find((i) => {
    if (i.approval_status !== "draft") return false;
    if (!i.is_active) return false;
    const isSelfCreated = user?.user_id && i.created_by &&
      String(i.created_by) === String(user.user_id);
    return !isSelfCreated;
  }) ?? null;

  // Draft input created by current user — waiting for peer approval
  const pendingPeerApproval = inputs.find((i) => {
    if (i.approval_status !== "draft") return false;
    if (!i.is_active) return false;
    return user?.user_id && i.created_by &&
      String(i.created_by) === String(user.user_id);
  }) ?? null;

  async function handleApprove(id) {
    setApproving(id);
    setApproveError(null);
    setApproveSuccess(false);
    try {
      const r = await fetch(`${API_BASE}/financial-plan-inputs/${id}/approve`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err?.error?.message ?? r.status);
      }
      setApproveSuccess(true);
      loadInputs(period.fiscal_period_id);
    } catch (err) {
      setApproveError(err.message);
    } finally {
      setApproving(null);
    }
  }

  return (
    <div className="space-y-4">
      <Card title="Payroll Review">
        <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] p-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Payroll export batches are created and approved by Comp Admin.
          Finance authorizes export by approving the financial plan input for the period.
        </div>

        <div className="mt-3">
          <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Payroll Export Authorization
          </p>
          {!period && !loading && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open period found.</p>
          )}
          {period && loading && <LoadingRow />}
          {period && error && <ErrorNote message="Unable to load authorization status." />}

          {period && !loading && !error && (
            <div className="space-y-3">
              {/* Authorization status */}
              <div className="flex items-center gap-2">
                {isAuthorized ? (
                  <Pill tone="Complete">Payroll export authorized</Pill>
                ) : (
                  <Pill tone="Not Ready">Pending Finance approval</Pill>
                )}
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {period.period_label ?? period.period_code}
                </span>
              </div>

              {/* Approved input details */}
              {isAuthorized && approvedInput && (
                <div className="rounded-xl border border-[rgb(var(--color-border))] p-3 grid gap-2 sm:grid-cols-3">
                  <Field label="Revenue Target"      value={fmt(approvedInput.revenue_target, approvedInput.budget_currency_code)} />
                  <Field label="Variable Comp Budget" value={fmt(approvedInput.variable_comp_budget, approvedInput.budget_currency_code)} />
                  <Field label="Source System"       value={approvedInput.source_system ?? "—"} />
                </div>
              )}

              {/* Approve action — shown when a peer-approvable draft exists */}
              {!isAuthorized && approvableInput && (
                <div className="rounded-xl border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning)/5)] p-3 space-y-3">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Field label="Revenue Target"      value={fmt(approvableInput.revenue_target, approvableInput.budget_currency_code)} />
                    <Field label="Variable Comp Budget" value={fmt(approvableInput.variable_comp_budget, approvableInput.budget_currency_code)} />
                    <Field label="Source System"       value={approvableInput.source_system ?? "—"} />
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => handleApprove(approvableInput.financial_plan_input_id)}
                      disabled={!!approving}
                    >
                      {approving === approvableInput.financial_plan_input_id
                        ? "Approving…"
                        : "Approve Financial Plan Input"}
                    </Button>
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      Approving will authorize payroll export for {period.period_label ?? period.period_code}
                    </span>
                  </div>
                </div>
              )}

              {/* Waiting for peer — current user created, needs different approver */}
              {!isAuthorized && pendingPeerApproval && !approvableInput && (
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Plan input submitted — waiting for a second Finance user to approve
                  (separation of duties, NN-36).
                </div>
              )}

              {/* No draft input exists at all */}
              {!isAuthorized && !approvableInput && !pendingPeerApproval && (
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  No financial plan input found for this period. Go to Plan Inputs to create one.
                </div>
              )}

              {approveSuccess && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
                  Financial plan input approved. Payroll export is now authorized.
                </div>
              )}
              {approveError && <ErrorNote message={approveError} />}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: Reconciliation — aggregate summary only (CC-1 / L2.8)
//
// Finance sees aggregate reconciliation counts and total variance only.
// Per-position payout data is comp_admin only — finance must never see
// individual position_id, expected_payout, or actual_payout (CC-1).
// Backend: GET /api/v1/reconciliation-summary (§17.5, S478 FA-01)
// ---------------------------------------------------------------------------

function Reconciliation() {
  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods?period_status=open`);
  const period   = periodsData?.data?.[0] ?? null;
  const periodId = period?.fiscal_period_id ?? null;

  const url = periodId ? `${API_BASE}/reconciliation-summary?fiscal_period_id=${periodId}` : null;
  const { data, loading, error } = useFetch(url);
  const summaries = data?.data ?? [];
  const summary   = summaries[0] ?? null;

  return (
    <div className="space-y-4">
      <Card title="Payroll Reconciliation">
        {/* CC-1 notice */}
        <div className="mb-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Aggregate view only. Individual position reconciliation detail is restricted
          to Comp Admin (CC-1 / L2.8).
        </div>

        {!periodId && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period.</p>}
        {periodId && loading && <LoadingRow />}
        {periodId && error && <ErrorNote message="Unable to load reconciliation data." />}
        {periodId && !loading && !error && !summary && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No reconciliation records for {period?.period_code ?? "this period"}.
          </p>
        )}

        {summary && (
          <div className="space-y-4">
            {/* Status tiles */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Total",    value: summary.total_count,   color: "text-[rgb(var(--color-text))]" },
                { label: "Matched",  value: summary.matched_count,  color: "text-[rgb(var(--color-success))]" },
                { label: "Mismatch", value: summary.mismatch_count, color: summary.mismatch_count > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-text))]" },
                { label: "Pending",  value: summary.pending_count,  color: summary.pending_count  > 0 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text))]" },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                  <p className={`text-tok-base font-bold ${color}`}>{value ?? "—"}</p>
                </div>
              ))}
            </div>

            {/* Aggregate financials */}
            <div className="grid gap-2 sm:grid-cols-2 rounded-xl border border-[rgb(var(--color-border))] p-3">
              <Field label="Total Payroll Accepted" value={fmt(summary.total_payroll_accepted)} />
              <Field label="Total Variance"         value={fmt(summary.total_variance)} />
            </div>

            {/* Mismatch alert */}
            {summary.mismatch_count > 0 && (
              <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-3 py-2.5">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-danger))]" />
                <p className="text-tok-sm text-[rgb(var(--color-text))]">
                  {summary.mismatch_count} reconciliation record{summary.mismatch_count !== 1 ? "s have" : " has"} a
                  variance. Contact Comp Admin for individual record details.
                </p>
              </div>
            )}

            {summary.mismatch_count === 0 && summary.matched_count > 0 && (
              <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-3 py-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
                <p className="text-tok-sm text-[rgb(var(--color-text))]">
                  All {summary.matched_count} reconciliation records matched.
                </p>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: Disputes (aggregate only — CC-1)
// ---------------------------------------------------------------------------

function DisputesAggregate() {
  const { data, loading, error } = useFetch(`${API_BASE}/disputes`);

  // Finance receives aggregate shape only — no individual records (CC-1).
  const aggregate = data?.aggregate ?? null;

  return (
    <div className="space-y-4">
      <Card
        title="Disputes"
        action={<Pill tone="Draft">Aggregate View</Pill>}
      >
        <div className="mb-2 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Individual dispute details are restricted to Comp Admin (CC-1).
        </div>
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load dispute data." />}
        {!loading && !error && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Total Disputes" value={aggregate?.total_count ?? "—"} />
            <Field label="Open" value={aggregate?.open_count ?? "—"} />
            <Field label="Closed" value={aggregate?.closed_count ?? "—"} />
          </div>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Panel: Population Scope (S402 R-UI-05)
//
// Read-only finance view of S402 position-level exception handling state.
// Finance has GET access to all three S402 endpoints per DR-022 §6.1/6.2 RBAC:
//   GET /api/v1/readiness/positions   — position eligibility counts + list
//   GET /api/v1/position-holds        — holds for the period
//   GET /api/v1/population-snapshots  — snapshot status for the period
//
// No write actions — all S402 write endpoints are comp_admin only.
// CC-1: positions here are scope governance records, not payout data — safe for finance.
// Governance actions (place hold, approve, create snapshot) are in CompAdminShell Stage 15.
// ---------------------------------------------------------------------------

function eligibilityTone(status) {
  return {
    eligible:      "Complete",
    ineligible:    "Blocked",
    hold_pending:  "Warning",
    hold_approved: "Warning",
    excluded:      "Blocked",
  }[status] ?? "Draft";
}

function snapshotStatusTone(status) {
  return {
    draft:            "Draft",
    pending_approval: "Warning",
    approved:         "Complete",
    superseded:       "Draft",
  }[status] ?? "Draft";
}

function PopulationScope() {
  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods?period_status=open`);
  const period   = periodsData?.data?.[0] ?? null;
  const periodId = period?.fiscal_period_id ?? null;

  // Position eligibility — read-only
  const { data: readinessData, loading: readinessLoading, error: readinessError } = useFetch(
    periodId ? `${API_BASE}/readiness/positions?fiscal_period_id=${periodId}&limit=200` : null
  );
  const positions       = readinessData?.positions ?? [];
  const totalEligible   = readinessData?.total_eligible   ?? null;
  const totalIneligible = readinessData?.total_ineligible ?? null;
  const totalHoldPending  = readinessData?.total_hold_pending  ?? 0;
  const totalHoldApproved = readinessData?.total_hold_approved ?? 0;

  // Holds — read-only
  const { data: holdsData, loading: holdsLoading, error: holdsError } = useFetch(
    periodId ? `${API_BASE}/position-holds?fiscal_period_id=${periodId}` : null
  );
  const holds = holdsData?.data ?? holdsData?.position_holds ?? [];

  // Snapshots — read-only
  const { data: snapshotsData, loading: snapshotsLoading, error: snapshotsError } = useFetch(
    periodId ? `${API_BASE}/population-snapshots?fiscal_period_id=${periodId}` : null
  );
  const snapshots = snapshotsData?.data ?? snapshotsData?.snapshots ?? [];
  const activeSnapshot = snapshots.find(
    (s) => ["draft", "pending_approval", "approved"].includes(s.snapshot_status)
  ) ?? null;

  const hasAssessment = totalEligible !== null;

  return (
    <div className="space-y-4">

      {/* Read-only notice */}
      <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
        <Eye className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          Read-only view. Position holds, snapshots, and eligibility assessments are
          managed by Comp Admin (Stage 15). Contact your comp admin to make changes.
        </p>
      </div>

      {/* Position Eligibility Summary */}
      <Card
        title="Position Eligibility"
        action={
          hasAssessment
            ? <Pill tone={totalIneligible > 0 ? "Warning" : "Complete"}>
                {totalEligible} eligible · {totalIneligible} ineligible
              </Pill>
            : null
        }
      >
        {!periodId && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period.</p>
        )}
        {readinessLoading && <LoadingRow />}
        {readinessError && <ErrorNote message="Unable to load position eligibility data." />}
        {!readinessLoading && !readinessError && !hasAssessment && periodId && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No position assessment on record for this period.
            Comp Admin must run "Assess Positions" from Readiness (Stage 15).
          </p>
        )}
        {hasAssessment && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Eligible",      value: totalEligible,     color: "text-[rgb(var(--color-success))]" },
                { label: "Ineligible",    value: totalIneligible,   color: totalIneligible   > 0 ? "text-[rgb(var(--color-danger))]"  : "text-[rgb(var(--color-text))]" },
                { label: "Hold pending",  value: totalHoldPending,  color: totalHoldPending  > 0 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text))]" },
                { label: "Hold approved", value: totalHoldApproved, color: totalHoldApproved > 0 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text))]" },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                  <p className={`text-tok-base font-bold ${color}`}>{value ?? "—"}</p>
                </div>
              ))}
            </div>
            {positions.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                {/* CC-1 / L2.8: position names and IDs not shown to finance.
                    Table shows role / segment / eligibility status only. */}
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      {["Role", "Segment", "Status", "Assessed"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {positions.map((pos, i) => (
                      <tr key={pos.position_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className="px-3 py-2 text-[rgb(var(--color-text))]">{pos.role_code ?? "—"}</td>
                        <td className="px-3 py-2 text-[rgb(var(--color-text-muted))]">{pos.segment_code ?? "—"}</td>
                        <td className="px-3 py-2">
                          <Pill tone={eligibilityTone(pos.eligibility_status)}>
                            {pos.eligibility_status?.replace(/_/g, " ")}
                          </Pill>
                        </td>
                        <td className="px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {pos.assessed_at ? new Date(pos.assessed_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Position Holds */}
      <Card
        title="Position Holds"
        action={
          holds.length > 0
            ? <Pill tone={holds.some((h) => h.hold_status === "pending") ? "Warning" : "Draft"}>
                {holds.length} hold{holds.length !== 1 ? "s" : ""}
              </Pill>
            : null
        }
      >
        {holdsLoading && <LoadingRow />}
        {holdsError && <ErrorNote message="Unable to load position holds." />}
        {!holdsLoading && !holdsError && holds.length === 0 && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No position holds for this period.</p>
        )}
        {holds.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
            <table className="min-w-full text-tok-sm border-collapse">
              {/* CC-1 / L2.8: position names and IDs not shown to finance. */}
              <thead>
                <tr>
                  {["Status", "Source", "Reason", "Created"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {holds.map((hold) => (
                  <tr key={hold.hold_id ?? hold.position_hold_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className="px-3 py-2">
                      <Pill tone={
                        hold.hold_status === "approved" ? "Warning"  :
                        hold.hold_status === "pending"  ? "Draft"    :
                        hold.hold_status === "released" ? "Complete" : "Draft"
                      }>
                        {hold.hold_status?.replace(/_/g, " ")}
                      </Pill>
                    </td>
                    <td className="px-3 py-2 text-[rgb(var(--color-text-muted))]">
                      {hold.hold_source === "system" ? "System" : "Comp admin"}
                    </td>
                    <td className="px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {Array.isArray(hold.reason_codes) ? hold.reason_codes.join(", ") : hold.reason_code ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {hold.created_at ? new Date(hold.created_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Population Snapshot */}
      <Card
        title="Population Snapshot"
        action={
          activeSnapshot
            ? <Pill tone={snapshotStatusTone(activeSnapshot.snapshot_status)}>
                {activeSnapshot.snapshot_status?.replace(/_/g, " ")}
              </Pill>
            : <Pill tone="Draft">No snapshot</Pill>
        }
      >
        {snapshotsLoading && <LoadingRow />}
        {snapshotsError && <ErrorNote message="Unable to load population snapshots." />}
        {!snapshotsLoading && !snapshotsError && !activeSnapshot && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No active population snapshot for this period. When a snapshot is approved,
            the engine will scope the calculation run to eligible positions only.
            Without a snapshot the engine runs the full population.
          </p>
        )}
        {activeSnapshot && (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-4">
              <Field label="Included" value={activeSnapshot.positions_included ?? "—"} />
              <Field label="Excluded" value={activeSnapshot.positions_excluded ?? "—"} />
              <Field label="Created"  value={activeSnapshot.created_at ? new Date(activeSnapshot.created_at).toLocaleString() : "—"} />
              <div>
                <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
                <Pill tone={snapshotStatusTone(activeSnapshot.snapshot_status)}>
                  {activeSnapshot.snapshot_status?.replace(/_/g, " ")}
                </Pill>
              </div>
            </div>
            {activeSnapshot.snapshot_status === "approved" && (
              <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-3 py-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
                <p className="text-tok-sm text-[rgb(var(--color-text))]">
                  Snapshot approved — next calculation run will use this population scope.
                  {activeSnapshot.positions_excluded > 0 && (
                    <> {activeSnapshot.positions_excluded} position{activeSnapshot.positions_excluded !== 1 ? "s" : ""} will be excluded.</>
                  )}
                </p>
              </div>
            )}
            {activeSnapshot.snapshot_status === "pending_approval" && (
              <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Awaiting approval from a second comp admin (NN-36).
                </p>
              </div>
            )}
            {activeSnapshot.population_hash && (
              <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">
                Hash: {activeSnapshot.population_hash.slice(0, 16)}…
              </p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// ScenarioExposure — P50/P75/P90 budget exposure panel (S139)
//
// Two sections:
//   1. Engine Output — live data from scenario_exposure_summary via
//      GET /api/v1/scenario-exposure?calculation_run_id={uuid}
//   2. Budget Planning — distribution pattern selector (client-side math)
//      3 presets + custom (mean + std dev). Illustrative only — no API call.
//      Helps CFO stress-test variable_comp_budget before period closes.
//
// RBAC: finance + comp_admin (server-enforced).
// Run selector: dropdown of completed runs from GET /api/v1/calculation-runs.
// ---------------------------------------------------------------------------

function normInv(p) {
  const a = [2.515517, 0.802853, 0.010328];
  const b = [1.432788, 0.189269, 0.001308];
  const t = Math.sqrt(-2 * Math.log(p < 0.5 ? p : 1 - p));
  const x = t - (a[0] + a[1] * t + a[2] * t * t) / (1 + b[0] * t + b[1] * t * t + b[2] * t * t * t);
  return p < 0.5 ? -x : x;
}

function normAtt(pct, mean, sd) {
  return Math.max(0, mean + normInv(pct) * sd);
}

function fmtPctDisplay(val) {
  if (val == null) return "—";
  return `${(Number(val) * 100).toFixed(1)}%`;
}

const DIST_PRESETS = [
  { id: "typical",    label: "Typical SaaS",    desc: "Most reps slightly below quota. A few overachievers drive the right tail.", mean: 0.85, sd: 0.25 },
  { id: "high",       label: "High Performance", desc: "Experienced team, clustered tightly around quota. Fewer outliers below.",   mean: 1.00, sd: 0.15 },
  { id: "developing", label: "Developing Team",  desc: "New team or market. Most reps below quota. Wide performance dispersion.",   mean: 0.70, sd: 0.30 },
  { id: "custom",     label: "Custom",           desc: "Enter your own historical attainment mean and standard deviation.",         mean: null, sd: null  },
];

function DistributionSelector({ selected, onSelect, customMean, customSd, onCustomMean, onCustomSd, budget }) {
  const activePreset = DIST_PRESETS.find((p) => p.id === selected) ?? DIST_PRESETS[0];
  const mean = selected === "custom" ? (parseFloat(customMean) / 100 || 0) : activePreset.mean;
  const sd   = selected === "custom" ? (parseFloat(customSd)   / 100 || 0) : activePreset.sd;

  const p50att = normAtt(0.50, mean, sd);
  const p75att = normAtt(0.75, mean, sd);
  const p90att = normAtt(0.90, mean, sd);

  const budgetNum = parseFloat(budget) || 0;
  const p50spend  = budgetNum * p50att;
  const p75spend  = budgetNum * p75att;
  const p90spend  = budgetNum * p90att;

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
        Select a team performance profile to model expected comp spend against your variable comp budget.
        This is illustrative only — no impact on engine calculations.
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {DIST_PRESETS.map((preset) => (
          <button
            key={preset.id}
            onClick={() => onSelect(preset.id)}
            className={`rounded-xl border p-3 text-left transition ${
              selected === preset.id
                ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))] text-white"
                : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:border-[rgb(var(--color-border-strong))]"
            }`}
          >
            <div className="mb-1 text-tok-sm font-semibold">{preset.label}</div>
            <div className={`text-tok-xs ${selected === preset.id ? "opacity-70" : "text-[rgb(var(--color-text-muted))]"}`}>
              {preset.id !== "custom"
                ? `Mean ${(preset.mean * 100).toFixed(0)}% · SD ${(preset.sd * 100).toFixed(0)}%`
                : "Custom parameters"}
            </div>
          </button>
        ))}
      </div>

      {selected === "custom" && (
        <div className="grid gap-3 sm:grid-cols-2 rounded-xl border border-[rgb(var(--color-border))] p-3">
          <div>
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Mean attainment %</label>
            <input
              type="number"
              value={customMean}
              onChange={(e) => onCustomMean(e.target.value)}
              placeholder="e.g. 85"
              min="0"
              max="200"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
            />
          </div>
          <div>
            <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Std deviation %</label>
            <input
              type="number"
              value={customSd}
              onChange={(e) => onCustomSd(e.target.value)}
              placeholder="e.g. 25"
              min="1"
              max="100"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
            />
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { label: "P50 Expected Spend", att: p50att, spend: p50spend },
          { label: "P75 Expected Spend", att: p75att, spend: p75spend },
          { label: "P90 Expected Spend", att: p90att, spend: p90spend },
        ].map(({ label, att, spend }) => (
          <div key={label} className="rounded-xl border border-[rgb(var(--color-border))] p-3">
            <div className="mb-1 text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</div>
            <div className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
              {spend > 0
                ? spend.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
                : "—"}
            </div>
            <div className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              At {fmtPctDisplay(att)} team attainment
            </div>
            {budgetNum > 0 && spend > 0 && (
              <div className={`mt-1 text-xs font-medium ${spend > budgetNum ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
                {spend > budgetNum
                  ? `${((spend - budgetNum) / budgetNum * 100).toFixed(1)}% over budget`
                  : `${((budgetNum - spend) / budgetNum * 100).toFixed(1)}% under budget`}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScenarioExposure() {
  const [runs, setRuns]                   = useState([]);
  const [runsLoading, setRunsLoading]     = useState(true);
  const [runsError, setRunsError]         = useState(null);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [exposure, setExposure]           = useState(null);
  const [expLoading, setExpLoading]       = useState(false);
  const [expError, setExpError]           = useState(null);
  const [distPreset, setDistPreset]       = useState("typical");
  const [customMean, setCustomMean]       = useState("85");
  const [customSd, setCustomSd]           = useState("25");

  useEffect(() => {
    fetch(`${API_BASE}/calculation-runs`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        const completed = (data?.calculation_runs ?? []).filter(
          (r) => r.run_status === "completed"
        );
        setRuns(completed);
        if (completed.length > 0) {
          setSelectedRunId(completed[0].calculation_run_id || completed[0].id);
        }
      })
      .catch((e) => setRunsError(e.message))
      .finally(() => setRunsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedRunId) return;
    setExpLoading(true);
    setExpError(null);
    setExposure(null);
    fetch(`${API_BASE}/scenario-exposure?calculation_run_id=${encodeURIComponent(selectedRunId)}`, {
      credentials: 'include',
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setExposure)
      .catch((e) => setExpError(e.message))
      .finally(() => setExpLoading(false));
  }, [selectedRunId]);

  const budget = exposure?.summary?.budget ?? "0";

  return (
    <div className="space-y-4">

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Calculation Run</label>
          {runsLoading && <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading runs…</div>}
          {runsError && <ErrorNote message="Unable to load calculation runs." />}
          {!runsLoading && !runsError && (
            <select
              value={selectedRunId}
              onChange={(e) => setSelectedRunId(e.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
            >
              {runs.length === 0 && <option value="">No completed runs</option>}
              {runs.map((r) => {
                const id = r.calculation_run_id || r.id;
                return (
                  <option key={id} value={id}>
                    {r.run_type === "final" ? "Final" : "Preview"} —{" "}
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : id.slice(0, 8)}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <div className="border-b border-[rgb(var(--color-border))] px-4 py-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
            <span className="text-sm font-semibold">Engine Output</span>
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Calculated P50 / P75 / P90 exposure</span>
          </div>
          {exposure && (
            <div className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {exposure.scenario_set_name} · {exposure.currency_code}
            </div>
          )}
        </div>

        <div className="p-4">
          {expLoading && <LoadingRow />}
          {expError && <ErrorNote message="Unable to load scenario exposure data." />}
          {!expLoading && !expError && !exposure && !selectedRunId && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Select a calculation run to view exposure.</p>
          )}

          {exposure && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] p-3">
                  <div className="text-tok-xs text-[rgb(var(--color-text-muted))]">Variable Comp Budget</div>
                  <div className="mt-1 text-tok-lg font-bold">
                    {Number(exposure.summary.budget).toLocaleString("en-US", { style: "currency", currency: exposure.currency_code, maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] p-3">
                  <div className="text-tok-xs text-[rgb(var(--color-text-muted))]">Revenue Target</div>
                  <div className="mt-1 text-tok-lg font-bold">
                    {Number(exposure.percentiles[0]?.revenue_target ?? 0).toLocaleString("en-US", { style: "currency", currency: exposure.currency_code, maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] p-3">
                  <div className="text-tok-xs text-[rgb(var(--color-text-muted))]">Scenario Set</div>
                  <div className="mt-1 text-tok-sm font-semibold">{exposure.scenario_set_code}</div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {exposure.percentiles.map((p) => {
                  const over = parseFloat(p.variance_vs_budget) > 0;
                  return (
                    <div key={p.scenario_definition_id} className="rounded-xl border border-[rgb(var(--color-border))] p-3">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">{p.percentile_label}</span>
                        <span className={`text-xs font-medium ${over ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
                          {over ? "▲" : "▼"} {Math.abs(parseFloat(p.variance_pct)).toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
                        {Number(p.projected_total_payout).toLocaleString("en-US", { style: "currency", currency: exposure.currency_code, maximumFractionDigits: 0 })}
                      </div>
                      <div className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                        {over ? "Over" : "Under"} budget by{" "}
                        {Math.abs(parseFloat(p.variance_vs_budget)).toLocaleString("en-US", { style: "currency", currency: exposure.currency_code, maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <div className="border-b border-[rgb(var(--color-border))] px-4 py-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-violet-500" />
            <span className="text-sm font-semibold">Budget Planning</span>
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Model expected spend by team performance profile</span>
          </div>
          <div className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Illustrative only — no impact on engine calculations
          </div>
        </div>
        <div className="p-4">
          <DistributionSelector
            selected={distPreset}
            onSelect={setDistPreset}
            customMean={customMean}
            customSd={customSd}
            onCustomMean={setCustomMean}
            onCustomSd={setCustomSd}
            budget={budget}
          />
        </div>
      </div>

    </div>
  );
}


// ---------------------------------------------------------------------------
// Panel: ScenarioPlanner (OI-S392-01 — built S393)
//
// Finance creates scenario sets per period. Comp_admin approves (NN-36 cross-role).
// Flow: POST /scenario-sets → POST /scenario-sets/{id}/definitions → comp_admin approves
//
// States:
//   - No sets: prompt to create
//   - Draft sets: show pending approval message, name comp_admin users explicitly
//   - Approved set: show active set details, block re-creation
//
// DD-04: shared/ imports only. DD-06: all values from live API.
// NN-COLUMNS: column names from ddl_summary_r38.md only.
// ---------------------------------------------------------------------------

const DEFAULT_DEFINITIONS = [
  {
    scenario_code: "P50",
    scenario_name: "Base Case",
    percentile_label: "P50",
    attainment_multiplier: "1.0000",
    credit_factor: "1.0000",
    budget_comparison_basis: "ARR",
    notes: "50th percentile — expected outcome at quota attainment",
  },
  {
    scenario_code: "P75",
    scenario_name: "Upside Case",
    percentile_label: "P75",
    attainment_multiplier: "1.2500",
    credit_factor: "1.0000",
    budget_comparison_basis: "ARR",
    notes: "75th percentile — above-quota performance with accelerators",
  },
  {
    scenario_code: "P90",
    scenario_name: "High Performance Case",
    percentile_label: "P90",
    attainment_multiplier: "1.5000",
    credit_factor: "1.0000",
    budget_comparison_basis: "ARR",
    notes: "90th percentile — top-decile attainment with full accelerator stack",
  },
];

// S482 Phase E: ScenarioPlanner now receives fiscalPeriodId and wizardSessionId
// as props from the shell. Internal period fetch removed (OI-SCENARIO-VERSIONING).
function ScenarioPlanner({ fiscalPeriodId, wizardSessionId }) {
  // Scenario sets for this period — approved_only=false to show drafts too
  const [sets, setSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(true);
  const [setsError, setSetsError] = useState(null);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [form, setForm] = useState({
    scenario_set_code: "",
    scenario_set_name: "",
    effective_date: "",
  });

  // Definitions — pre-populated with defaults, editable before submit
  const [definitions, setDefinitions] = useState(DEFAULT_DEFINITIONS);

  // Definitions re-submit feedback on existing draft
  const [defsSubmitting, setDefsSubmitting] = useState(null);
  const [defsError, setDefsError] = useState(null);
  const [defsSuccess, setDefsSuccess] = useState(null);

  // S482 Phase E — Amendment state for governed new-version flow
  const [amendModalOpen, setAmendModalOpen]       = useState(false);
  const [amendmentId, setAmendmentId]             = useState(null);
  const [pendingCreateVersion, setPendingCreateVersion] = useState(false);

  function loadSets() {
    if (!fiscalPeriodId) return;
    setSetsLoading(true);
    setSetsError(null);
    fetch(
      `${API_BASE}/scenario-sets?fiscal_period_id=${fiscalPeriodId}&approved_only=false&active_only=false`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setSets(d.scenario_sets ?? []))
      .catch((e) => setSetsError(e.message))
      .finally(() => setSetsLoading(false));
  }

  useEffect(() => { loadSets(); }, [fiscalPeriodId]);

  function handleFormChange(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function handleDefChange(idx, field, value) {
    setDefinitions((prev) =>
      prev.map((d, i) => i === idx ? { ...d, [field]: value } : d)
    );
  }

  // S482 Phase E — "Create New Version" entry point.
  // When an approved set exists, fire AmendmentBeginModal before opening the
  // create form. entityId is the approved set's scenario_set_id — REQUIRED,
  // never null (hard rule: AmendmentBeginModal entityId must not be null).
  function handleCreateNewVersion() {
    if (!approvedSet) {
      // No approved set — just open create form directly (v1 flow)
      setShowCreate(true);
      return;
    }
    // Open amendment modal; entityId = approvedSet.scenario_set_id (never null)
    setAmendModalOpen(true);
    setPendingCreateVersion(true);
  }

  // S482 Phase E — Called by AmendmentBeginModal on successful amendment begin.
  // Stores the amendment_id and opens the create form for the new version.
  function handleAmendmentBegin(newAmendmentId) {
    setAmendmentId(newAmendmentId);
    setAmendModalOpen(false);
    setPendingCreateVersion(false);
    setShowCreate(true);
  }

  const approvedSet  = sets.find((s) => s.approved_at);
  const draftSets    = sets.filter((s) => !s.approved_at);

  // S482 Phase E — version pre-population per spec
  const nextVersion     = approvedSet ? (approvedSet.version ?? 1) + 1 : 1;
  const suggestedCode   = approvedSet
    ? `${approvedSet.scenario_set_code}-V${nextVersion}`
    : "";

  async function handleCreate() {
    if (!fiscalPeriodId) return;
    if (!form.scenario_set_code.trim() || !form.scenario_set_name.trim() || !form.effective_date) {
      setCreateError("Code, name, and effective date are required.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      // Step 1 — create scenario set header with version pre-populated
      const r1 = await fetch(`${API_BASE}/scenario-sets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          fiscal_period_id:  fiscalPeriodId,
          scenario_set_code: form.scenario_set_code.trim(),
          scenario_set_name: form.scenario_set_name.trim(),
          effective_date:    form.effective_date,
          is_active:         true,
          version:           nextVersion,  // S482: pre-populated from approvedSet.version + 1
        }),
      });
      if (!r1.ok) {
        const err = await r1.json();
        throw new Error(err?.detail?.error?.message ?? String(r1.status));
      }
      const created = await r1.json();
      const ssId = created.scenario_set_id;

      // Step 2 — post P50/P75/P90 definitions
      const r2 = await fetch(`${API_BASE}/scenario-sets/${ssId}/definitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          definitions: definitions.map((d) => ({
            ...d,
            attainment_multiplier: parseFloat(d.attainment_multiplier),
            credit_factor:         parseFloat(d.credit_factor),
          })),
        }),
      });
      if (!r2.ok) {
        const err = await r2.json();
        throw new Error(err?.detail?.error?.message ?? String(r2.status));
      }

      // Reset form, clear amendment state, and reload
      setShowCreate(false);
      setAmendmentId(null);
      setForm({ scenario_set_code: "", scenario_set_name: "", effective_date: "" });
      setDefinitions(DEFAULT_DEFINITIONS);
      loadSets();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  // Re-submit definitions on an existing draft (idempotent upsert on the router)
  async function handleResubmitDefs(ssId) {
    setDefsSubmitting(ssId);
    setDefsError(null);
    setDefsSuccess(null);
    try {
      const r = await fetch(`${API_BASE}/scenario-sets/${ssId}/definitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          definitions: definitions.map((d) => ({
            ...d,
            attainment_multiplier: parseFloat(d.attainment_multiplier),
            credit_factor:         parseFloat(d.credit_factor),
          })),
        }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err?.detail?.error?.message ?? String(r.status));
      }
      setDefsSuccess(ssId);
      loadSets();
    } catch (err) {
      setDefsError(err.message);
    } finally {
      setDefsSubmitting(null);
    }
  }

  return (
    <div className="space-y-4">

      {/* S482 Phase E — AmendmentBeginModal for governed new-version flow.
          entityId = approvedSet.scenario_set_id — REQUIRED, never null (hard rule).
          entityType = "scenario_definition" — matches governed entity type. */}
      {approvedSet && (
        <AmendmentBeginModal
          open={amendModalOpen}
          entityType="scenario_definition"
          entityId={approvedSet.scenario_set_id}
          fiscalPeriodId={fiscalPeriodId}
          wizardSessionId={wizardSessionId}
          onBegin={handleAmendmentBegin}
          onCancel={() => {
            setAmendModalOpen(false);
            setPendingCreateVersion(false);
          }}
        />
      )}

      {/* S482 Phase E — Open amendment banner (amber), mirrors ScenarioSetBuilder.jsx lines 335-343 */}
      {amendmentId && (
        <div className="flex items-center gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3">
          <span className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
            Amendment Open
          </span>
          <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            A scenario_definition amendment is open for this period. Complete the new version below to commit it.
          </span>
        </div>
      )}

      {/* ── Active approved scenario set ── */}
      <Card title="Active Scenario Set">
        {setsLoading && <LoadingRow />}
        {setsError && <ErrorNote message="Unable to load scenario sets." />}
        {!setsLoading && !setsError && !approvedSet && (
          <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-4 py-3">
            <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">
              ⚠️ No approved scenario set for this period.
            </p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Create a scenario set below and submit it for comp_admin approval.
              Scenario sets enable the Exposure Monitor to model P50/P75/P90 spend projections.
            </p>
          </div>
        )}
        {approvedSet && (
          <div className="mt-2 grid gap-3 rounded-xl border border-[rgb(var(--color-border))] p-3 sm:grid-cols-4">
            <Field label="Code"    value={approvedSet.scenario_set_code} />
            <Field label="Name"    value={approvedSet.scenario_set_name} />
            <Field label="Period"  value={approvedSet.period_code ?? String(approvedSet.fiscal_period_id)} />
            <div>
              <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
              <Pill tone="Complete">Approved</Pill>
            </div>
            <Field label="Version"        value={`v${approvedSet.version ?? 1}`} />
            <Field label="Effective Date" value={approvedSet.effective_date ?? "—"} />
            <Field
              label="Definitions"
              value={`${approvedSet.definition_count} (${(approvedSet.definition_codes ?? []).join(", ")})`}
            />
            <Field
              label="Approved"
              value={approvedSet.approved_at ? new Date(approvedSet.approved_at).toLocaleDateString() : "—"}
            />
            <div>
              <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Runnable</p>
              <Pill tone={approvedSet.is_runnable ? "Complete" : "Not Ready"}>
                {approvedSet.is_runnable ? "Yes — engine unlocked" : "Not yet"}
              </Pill>
            </div>
          </div>
        )}
      </Card>

      {/* ── Draft sets awaiting comp_admin approval ── */}
      {draftSets.length > 0 && (
        <Card title="Pending Approval">
          {/* S482: removed hardcoded "Riley Santos" / "Devon Park" names per hard rule */}
          <div className="mb-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            These scenario sets are awaiting comp_admin approval.
            Actor separation is enforced — the creator cannot self-approve (NN-36).
            Ask a comp_admin to log in to Finance View and approve this scenario set.
          </div>
          {draftSets.map((s) => (
            <div
              key={s.scenario_set_id}
              className="mt-3 grid gap-3 rounded-xl border border-[rgb(var(--color-border))] p-3 sm:grid-cols-4"
            >
              <Field label="Code"    value={s.scenario_set_code} />
              <Field label="Name"    value={s.scenario_set_name} />
              <Field label="Version" value={`v${s.version ?? 1}`} />
              <Field
                label="Definitions"
                value={
                  s.definition_count > 0
                    ? `${s.definition_count} (${(s.definition_codes ?? []).join(", ")})`
                    : "None yet"
                }
              />
              <div>
                <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
                <Pill tone="Draft">Pending Approval</Pill>
              </div>
            </div>
          ))}
          {defsError  && <ErrorNote message={defsError} />}
          {defsSuccess && (
            <p className="mt-2 text-tok-sm text-[rgb(var(--color-success))]">
              Definitions updated successfully.
            </p>
          )}
        </Card>
      )}

      {/* ── Create new scenario set / new version ── */}
      <Card title={approvedSet ? `Create New Version (v${nextVersion})` : "Create Scenario Set"}>
        {/* S482 Phase E: replaced dead-end static <p> with "Create New Version" button */}
        {approvedSet && !showCreate && (
          <div className="space-y-3">
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              An approved scenario set (v{approvedSet.version ?? 1}) exists for this period.
              To update exposure assumptions, create a new version — the existing approved set
              will be superseded automatically on comp_admin approval.
            </p>
            <Button onClick={handleCreateNewVersion} disabled={!!amendmentId && showCreate}>
              Create New Version → (v{nextVersion})
            </Button>
          </div>
        )}
        {!approvedSet && !showCreate && (
          <div className="space-y-3">
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              No approved scenario set yet. Create one to enable spend projection modelling for this period.
            </p>
            <Button onClick={() => setShowCreate(true)}>
              New Scenario Set
            </Button>
          </div>
        )}

        {showCreate && (
          <div className="space-y-4">

            {/* S482 Phase E — version info banner when creating a new version */}
            {approvedSet && (
              <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Creating <strong>v{nextVersion}</strong> — supersedes the current approved v{approvedSet.version ?? 1} ({approvedSet.scenario_set_code}) on comp_admin approval.
              </div>
            )}

            {/* Header fields */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Scenario Set Code <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  value={form.scenario_set_code || suggestedCode}
                  onChange={(e) => handleFormChange("scenario_set_code", e.target.value)}
                  placeholder={suggestedCode || "e.g. FY2026-M01-BASE"}
                  maxLength={40}
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
                />
              </div>
              <div>
                <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Scenario Set Name <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  value={form.scenario_set_name}
                  onChange={(e) => handleFormChange("scenario_set_name", e.target.value)}
                  placeholder="e.g. FY2026 M01 Base Scenario"
                  maxLength={120}
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
                />
              </div>
              <div>
                <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Effective Date <span className="text-[rgb(var(--color-danger))]">*</span>
                </label>
                <input
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => handleFormChange("effective_date", e.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
                />
              </div>
            </div>

            {/* P50 / P75 / P90 definitions — editable, pre-populated */}
            <div>
              <p className="mb-2 text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                Scenario Definitions — P50 / P75 / P90
              </p>
              <div className="mb-2 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Pre-populated with standard defaults. Adjust attainment multipliers to reflect
                this period's payout trajectory assumptions. All three scenarios are required
                before comp_admin can approve.
              </div>
              <div className="space-y-3">
                {definitions.map((def, idx) => (
                  <div
                    key={def.scenario_code}
                    className="grid gap-3 rounded-xl border border-[rgb(var(--color-border))] p-3 sm:grid-cols-5"
                  >
                    <div>
                      <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Scenario</p>
                      <Pill tone={idx === 0 ? "Complete" : idx === 1 ? "Draft" : "Blocked"}>
                        {def.percentile_label}
                      </Pill>
                    </div>
                    <div>
                      <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Name</label>
                      <input
                        value={def.scenario_name}
                        onChange={(e) => handleDefChange(idx, "scenario_name", e.target.value)}
                        className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                        Attainment Multiplier
                      </label>
                      <input
                        type="number"
                        step="0.0001"
                        min="0.0001"
                        value={def.attainment_multiplier}
                        onChange={(e) => handleDefChange(idx, "attainment_multiplier", e.target.value)}
                        className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                        Credit Factor
                      </label>
                      <input
                        type="number"
                        step="0.0001"
                        min="0.0001"
                        value={def.credit_factor}
                        onChange={(e) => handleDefChange(idx, "credit_factor", e.target.value)}
                        className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Notes</label>
                      <input
                        value={def.notes ?? ""}
                        onChange={(e) => handleDefChange(idx, "notes", e.target.value)}
                        className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {createError && <ErrorNote message={createError} />}

            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={creating || !fiscalPeriodId}>
                {creating ? "Creating…" : "Create & Submit for Approval"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreate(false);
                  setCreateError(null);
                  setDefinitions(DEFAULT_DEFINITIONS);
                  setAmendmentId(null);   // S482: clear amendment state on cancel
                  setForm({ scenario_set_code: "", scenario_set_name: "", effective_date: "" });
                }}
                disabled={creating}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Stub: ScenarioHistory (future session)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ScenarioHistory — S480 Phase C (OI-SCENARIO-HISTORY-PANEL)
//
// Layer 1: Wire GET /api/v1/scenario-sets — fiscal period label, scenario set
//   code/name, definition count, approval status, approved_at.
//   Ordered by fiscal_period_id DESC (API default).
//   Empty state: "No scenario sets have been created yet."
//
// Layer 2 rows: "Pending closed period data" placeholder per spec.
//   Accuracy vs Actual column deferred to Phase D endpoint
//   (GET /api/v1/scenario-history-accuracy). No "Coming soon" text.
//
// RBAC: finance, comp_admin — server enforced.
// Gate: feature.tier.scenario_modelling — server enforced.
// ---------------------------------------------------------------------------

function ScenarioHistory() {
  // Fetch all scenario sets for this tenant — not filtered to open period,
  // history shows all periods ordered by fiscal_period_id DESC (API default).
  const { data, loading, error } = useFetch(
    `${API_BASE}/scenario-sets?approved_only=false&active_only=false`
  );
  const sets = data?.scenario_sets ?? [];

  return (
    <div className="space-y-4">
      <Card title="Scenario History">
        {/* Context note */}
        <div className="mb-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          All scenario sets across periods — most recent first. Accuracy vs Actual
          comparison will populate once periods are closed and actuals are available.
        </div>

        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load scenario history." />}

        {!loading && !error && sets.length === 0 && (
          <div className="rounded-xl border border-[rgb(var(--color-border))] p-6 text-center">
            <History className="mx-auto mb-2 h-6 w-6 text-[rgb(var(--color-text-muted))]" />
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              No scenario sets have been created yet. Create one in Scenario Planner.
            </p>
          </div>
        )}

        {!loading && !error && sets.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
            <table className="min-w-full text-tok-sm border-collapse">
              <thead>
                <tr>
                  {[
                    "Fiscal Period",
                    "Scenario Code",
                    "Scenario Name",
                    "Definitions",
                    "Status",
                    "Approved",
                    "Accuracy vs Actual",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {sets.map((s) => (
                  <tr
                    key={s.scenario_set_id}
                    className="hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                  >
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                      {s.period_label ?? s.period_code ?? `ID ${s.fiscal_period_id}`}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-tok-xs text-[rgb(var(--color-text))] whitespace-nowrap">
                      {s.scenario_set_code}
                    </td>
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text))]">
                      {s.scenario_set_name}
                    </td>
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                      {s.definition_count > 0
                        ? `${s.definition_count} (${(s.definition_codes ?? []).join(", ")})`
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {s.approved_at ? (
                        <Pill tone="Complete">Approved</Pill>
                      ) : (
                        <Pill tone="Warning">Pending Approval</Pill>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                      {s.approved_at
                        ? new Date(s.approved_at).toLocaleDateString()
                        : "—"}
                    </td>
                    {/* Layer 2 — Accuracy vs Actual: pending Phase D endpoint */}
                    <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                      Pending closed period data
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stub: PlanVersions (future session)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// PlanVersions — S480 Phase C (OI-PLAN-VERSIONS-PANEL)
//
// Reads GET /api/v1/financial-plan-version-history — DISTINCT from
// p4_plan_versioning_router.py which reads plan_version_history (comp_admin
// domain). This panel reads financial_plan_version_history (Finance domain).
//
// Timeline display per spec:
//   - version badge
//   - variable_comp_budget formatted
//   - budget_delta colour-coded: green=decrease, amber=>5% increase, red=>15% increase
//   - revision_reason as primary text
//   - approved_by_name + approved_at as secondary
//
// Optional filter: active period fiscal_period_id via useFetch URL.
// Empty state: "No approved AOP versions found for this period."
//
// RBAC: finance, comp_admin — server enforced.
// Gate: feature.tier.finance_shell — server enforced.
// ---------------------------------------------------------------------------

function PlanVersions() {
  // Reads from aop_input via GET /api/v1/aop-inputs (financial_alignment_router §17.4).
  // financial_plan_version_history is never populated — aop_input IS the AOP plan data.
  // Shows planned variable comp and headcount by role for the selected period.
  // Period switcher covers all periods — Finance can compare M01 vs M04 AOP evolution.

  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods`);
  const allPeriods = (periodsData?.data ?? [])
    .slice().sort((a, b) => b.fiscal_period_id - a.fiscal_period_id);

  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  // OI-FINANCE-GOVERNANCE-PERIOD-DEFAULT: default to earliest open period, not M12.
  // allPeriods is sorted DESC for dropdown display (most recent first) so allPeriods[0]
  // was M12. Resolve the correct default using the same logic as the shell:
  // earliest open → earliest non-locked → earliest overall.
  useEffect(() => {
    if (selectedPeriodId === null && allPeriods.length > 0) {
      const open = allPeriods.filter((p) => p.period_status === "open");
      const candidates = open.length
        ? open
        : allPeriods.filter((p) => p.period_status !== "locked").length
          ? allPeriods.filter((p) => p.period_status !== "locked")
          : allPeriods;
      const earliest = candidates.reduce((e, p) =>
        p.period_start_date < e.period_start_date ? p : e
      );
      setSelectedPeriodId(earliest.fiscal_period_id);
    }
  }, [allPeriods.length]);

  const { data, loading, error } = useFetch(
    selectedPeriodId ? `${API_BASE}/aop-inputs?fiscal_period_id=${selectedPeriodId}` : null
  );
  const rows = data?.data ?? [];

  const totalPlannedVC  = rows.reduce((s, r) => s + Number(r.total_planned_variable_comp ?? 0), 0);
  const totalHeadcount  = rows.reduce((s, r) => s + Number(r.total_planned_headcount ?? 0), 0);
  const selectedPeriod  = allPeriods.find(p => p.fiscal_period_id === selectedPeriodId);

  return (
    <div className="space-y-4">
      <Card
        title="AOP Plan Inputs"
        action={
          allPeriods.length > 0 && (
            <select
              value={selectedPeriodId ?? ""}
              onChange={(e) => setSelectedPeriodId(Number(e.target.value))}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              {allPeriods.map((p) => (
                <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
                  {p.period_label ?? p.period_code}
                </option>
              ))}
            </select>
          )
        }
      >
        <div className="mb-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Role-level planned variable comp and headcount loaded monthly via AOP import.
          Compare periods to track plan evolution.
        </div>

        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load AOP inputs." />}

        {!loading && !error && rows.length === 0 && (
          <div className="rounded-xl border border-[rgb(var(--color-border))] p-6 text-center">
            <Layers className="mx-auto mb-2 h-6 w-6 text-[rgb(var(--color-text-muted))]" />
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              No AOP inputs loaded for {selectedPeriod?.period_label ?? selectedPeriod?.period_code ?? "this period"}.
              Load via the Comp Admin AOP import.
            </p>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            {/* Totals summary */}
            <div className="mb-3 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Total Planned VC</p>
                <p className="text-tok-base font-bold text-[rgb(var(--color-text))]">{fmt(totalPlannedVC)}</p>
              </div>
              <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Total Headcount</p>
                <p className="text-tok-base font-bold text-[rgb(var(--color-text))]">{totalHeadcount}</p>
              </div>
              <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Avg VC per Head</p>
                <p className="text-tok-base font-bold text-[rgb(var(--color-text))]">
                  {totalHeadcount > 0 ? fmt(totalPlannedVC / totalHeadcount) : "—"}
                </p>
              </div>
            </div>

            {/* Role breakdown table */}
            <div className="overflow-x-auto">
              <table className="w-full text-tok-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--color-border))]">
                    {["Role", "Segment", "Business Unit", "Planned VC", "Headcount", "Avg VC / Head"].map((h) => (
                      <th key={h} className="pb-2 pr-4 text-left text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.aop_input_id ?? `${row.role_code}-${row.segment_code}`}
                      className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className="py-2.5 pr-4 font-medium text-[rgb(var(--color-text))]">{row.role_code ?? row.role_name ?? "—"}</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">{row.segment_code ?? row.segment_name ?? "—"}</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))]">{row.bu_code ?? row.bu_name ?? "—"}</td>
                      <td className="py-2.5 pr-4 tabular-nums font-medium text-[rgb(var(--color-text))]">{fmt(row.total_planned_variable_comp, row.currency_code)}</td>
                      <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text-muted))]">{row.total_planned_headcount ?? "—"}</td>
                      <td className="py-2.5 tabular-nums text-[rgb(var(--color-text-muted))]">
                        {row.total_planned_headcount > 0
                          ? fmt(Number(row.total_planned_variable_comp) / Number(row.total_planned_headcount), row.currency_code)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="bg-[rgb(var(--color-surface-muted))] font-semibold">
                    <td className="py-2.5 pr-4 text-[rgb(var(--color-text))]" colSpan={3}>Total</td>
                    <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text))]">{fmt(totalPlannedVC)}</td>
                    <td className="py-2.5 pr-4 tabular-nums text-[rgb(var(--color-text))]">{totalHeadcount}</td>
                    <td className="py-2.5 tabular-nums text-[rgb(var(--color-text-muted))]">
                      {totalHeadcount > 0 ? fmt(totalPlannedVC / totalHeadcount) : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// CopilotInline removed S241 — replaced by shared CopilotPanel.

// ---------------------------------------------------------------------------
// PeriodSelector — S607 OI-FINANCE-PERIOD-SELECTOR-DIVERGENCE
// Mirrors CompAdmin PeriodSelector (S602) exactly.
// Receives already-fetched periods array from shell — no fetch inside.
// Groups by fiscal_year_id for multi-year tenants.
// statusSuffix shows "· locked" / "· closed" for non-open periods.
// ---------------------------------------------------------------------------

function PeriodSelector({ periods, selectedPeriodId, defaultPeriod, onSelect }) {
  if (!periods || periods.length === 0) return null;

  const currentId = selectedPeriodId ?? defaultPeriod?.fiscal_period_id;

  const grouped = periods.reduce((acc, p) => {
    const yr = p.fiscal_year_id ?? "other";
    if (!acc[yr]) acc[yr] = [];
    acc[yr].push(p);
    return acc;
  }, {});

  const yearKeys = Object.keys(grouped).sort();
  const multiYear = yearKeys.length > 1;

  function statusSuffix(p) {
    if (p.period_status === "locked") return " · locked";
    if (p.period_status === "closed") return " · closed";
    return "";
  }

  return (
    <select
      value={currentId ?? ""}
      onChange={(e) => {
        const val = e.target.value;
        onSelect(val ? Number(val) : null);
      }}
      className="rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text))] focus:outline-none cursor-pointer"
      aria-label="Select fiscal period"
    >
      {multiYear
        ? yearKeys.map((yr) => (
            <optgroup key={yr} label={`FY ${yr}`}>
              {grouped[yr].map((p) => (
                <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
                  {p.period_label ?? p.period_code}{statusSuffix(p)}
                </option>
              ))}
            </optgroup>
          ))
        : periods.map((p) => (
            <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
              {p.period_label ?? p.period_code}{statusSuffix(p)}
            </option>
          ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Finance ContextHeader (spec §6.3)
// S607: period selector lifted to shell — passed as props, mirrors CompAdmin.
// ---------------------------------------------------------------------------

// OI-S920-FINANCE-PLAN-INPUT-DEPENDENCY-NOT-SURFACED
// hasApprovedPlanInput: null (loading) | false (missing) | true (present)
// Banner renders only on explicit false — null suppresses to avoid loading flash.
function FinanceContextHeader({ pageTitle, periods, selectedPeriodId, defaultPeriod, isNonDefaultPeriod, period, onPeriodSelect, hasApprovedPlanInput }) {
  return (
    <div className="mb-4 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">{pageTitle}</h1>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            {periods && periods.length > 0 && (
              <PeriodSelector
                periods={periods}
                selectedPeriodId={selectedPeriodId}
                defaultPeriod={defaultPeriod}
                onSelect={onPeriodSelect}
              />
            )}
            {isNonDefaultPeriod && period && (
              <span className="inline-flex items-center gap-1 rounded-md bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-warning))] border border-[rgb(var(--color-warning))]/30">
                Not the active period
                {period.period_status !== "open" && (
                  <span className="font-normal opacity-80">· {period.period_status}</span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
      {hasApprovedPlanInput === false && (
        // OI-S920: No approved Finance Plan Input for this period.
        // Payroll export is blocked and alignment metrics are unavailable until
        // a plan input is created and approved by a second Finance user (NN-36).
        // Banner is non-dismissable — disappears automatically once approved.
        <div className="flex items-start gap-2.5 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          <div className="space-y-0.5">
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              No approved Finance Plan Input for this period
            </p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
              Revenue targets, VC budget, and payroll export authorization are unavailable until a Finance Plan Input
              is created and approved by a second Finance user.
              Go to <span className="font-medium text-[rgb(var(--color-text))]">Plan Governance → Finance Budget Commitment</span> to create one.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Finance Nav
// ---------------------------------------------------------------------------

// S608: restructured from 12-panel nav to 4-page CFO workflow.
// All old routes preserved as <Navigate> redirects in the Routes block below.
const NAV_ITEMS = [
  { to: "/finance/dashboard",           label: "Period Dashboard",    icon: LayoutDashboard },
  { to: "/finance/governance",          label: "Plan Governance",     icon: BookOpen },
  { to: "/finance/close",               label: "Period Close",        icon: CheckSquare },
  { to: "/finance/scenarios",           label: "Scenario Planning",   icon: BarChart2 },
  // S801 — New-architecture routes (old routes above preserved until these are proven)
  { to: "/finance/financial-control",   label: "Financial Control",   icon: Layers3 },
  { to: "/finance/period-close-v2",     label: "Close Workflow",      icon: ClipboardCheck },
  { to: "/finance/settlements",         label: "Settlements",         icon: WalletCards },
];

function FinanceNav({ mobileOpen, onClose }) {
  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={onClose} />
      )}
      <aside className={`fixed inset-y-0 left-0 z-40 w-52 overflow-y-auto border-r border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pt-16 lg:static lg:pt-0 transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <nav className="p-3 space-y-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-tok-sm font-medium transition ${
                  isActive
                    ? "bg-[rgb(var(--color-primary))] text-white"
                    : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page title map
// ---------------------------------------------------------------------------

// S608: 4-page CFO workflow. Old route titles removed — those paths now redirect.
const PAGE_TITLES = {
  "/finance/dashboard":          "Period Dashboard",
  "/finance/governance":         "Plan Governance",
  "/finance/close":              "Period Close",
  "/finance/scenarios":          "Scenario Planning",
  // S801 — New-architecture routes
  "/finance/financial-control":  "Financial Control",
  "/finance/period-close-v2":    "Close Workflow",
  "/finance/settlements":        "Settlements",
};

// ---------------------------------------------------------------------------
// FinanceShell root
// ---------------------------------------------------------------------------

export default function FinanceShell() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(true);

  // ── Period state — lifted to shell (S607), mirrors CompAdmin S602 exactly ──
  // Fetch all periods without filter — same as CompAdmin (active period can be locked).
  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods`);
  const periods = periodsData?.data ?? periodsData?.fiscal_periods ?? [];
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  // Default period resolution — identical to CompAdmin S580/S602 logic.
  // Prefer earliest open period; fall back to earliest non-locked; fall back to earliest.
  const defaultPeriod = (() => {
    if (!periods.length) return null;
    const open = periods.filter((p) => p.period_status === "open");
    if (open.length) {
      return open.reduce((earliest, p) =>
        p.period_start_date < earliest.period_start_date ? p : earliest
      );
    }
    const nonLocked = periods.filter((p) => p.period_status !== "locked");
    if (nonLocked.length) {
      return nonLocked.reduce((earliest, p) =>
        p.period_start_date < earliest.period_start_date ? p : earliest
      );
    }
    return periods.reduce((earliest, p) =>
      p.period_start_date < earliest.period_start_date ? p : earliest
    );
  })();

  // Single source of truth for active period — user selection overrides default.
  const period = selectedPeriodId
    ? (periods.find((p) => p.fiscal_period_id === selectedPeriodId) ?? defaultPeriod)
    : defaultPeriod;

  // Non-default warning — shown in context header when user has switched periods.
  const isNonDefaultPeriod =
    selectedPeriodId !== null &&
    defaultPeriod !== null &&
    selectedPeriodId !== defaultPeriod.fiscal_period_id;

  const fiscalPeriodId = period?.fiscal_period_id ?? null;

  // ── Shell-level data fetches for FinancialAlignment ───────────────────────
  // Fetched once at shell level, passed as props — avoids per-period re-fetch
  // inside the component and supports multi-period chart series.
  //
  // statement-batches: no fiscal_period_id filter — client-side filter in component
  // (fix for OI-FINANCE-ALIGNMENT-ACTUAL-NOT-SHOWING: server filter unreliable).
  // financial-plan-inputs: no filter — all periods returned, component filters.
  // aop-inputs: no filter — all periods, component filters.
  // financial-alignment: all periods — provides total_credits_reporting + active_position_count
  //   when backend field is present (OI-FINANCE-CREDITS-AGGREGATE-ENDPOINT).
  const { data: allBatchesData }      = useFetch(`${API_BASE}/statement-batches`);
  const { data: allPlanInputsData }   = useFetch(`${API_BASE}/financial-plan-inputs`);
  const { data: allAopData }          = useFetch(`${API_BASE}/aop-inputs`);
  const { data: allAlignmentData }    = useFetch(`${API_BASE}/financial-alignment`);

  const allBatches       = allBatchesData?.data ?? [];
  const allPlanInputs    = allPlanInputsData?.data ?? [];
  const allAopRows       = allAopData?.data ?? [];

  // ── OI-S920-FINANCE-PLAN-INPUT-DEPENDENCY-NOT-SURFACED ────────────────────
  // Derive approved plan input status for the active period.
  // allPlanInputs is already fetched at shell level — no new fetch needed.
  // Three states:
  //   null  — period not yet resolved (loading) — banner suppressed to avoid flash
  //   false — period resolved, no approved input — banner shown
  //   true  — approved input exists — banner hidden
  // Condition mirrors PayrollReview.isAuthorized and PeriodDashboard.approvedInput
  // to stay consistent with existing approval semantics across the shell.
  const hasApprovedPlanInput = fiscalPeriodId === null
    ? null
    : allPlanInputs.some(
        (i) =>
          i.fiscal_period_id === fiscalPeriodId &&
          i.is_active &&
          i.approval_status === "approved"
      );

  // financial-alignment may return array or single object — normalise to array
  const allAlignmentRows = Array.isArray(allAlignmentData?.data)
    ? allAlignmentData.data
    : allAlignmentData?.data
      ? [allAlignmentData.data]
      : [];

  // ── S608 Step 2 — PeriodDashboard additional shell-level fetches ─────────
  // scenario-exposure/by-role: auto-selects most recent final run for the period.
  // No run selector in Dashboard (product owner decision: silent auto-select).
  // Fetched without calculation_run_id — router resolves most recent final run.
  // plan-vs-loaded: quota loaded vs AOP plan per role/segment/BU.
  // Both fetched at shell level — passed as props, no re-fetch on filter change.
  const { data: exposureByRoleData } = useFetch(
    fiscalPeriodId
      ? `${API_BASE}/scenario-exposure/by-role?fiscal_period_id=${fiscalPeriodId}`
      : null
  );
  const { data: planVsLoadedData } = useFetch(
    fiscalPeriodId
      ? `${API_BASE}/plan-vs-loaded?fiscal_period_id=${fiscalPeriodId}`
      : null
  );
  const allExposureByRole = exposureByRoleData?.data ?? [];
  const allPlanVsLoaded   = planVsLoadedData?.data   ?? [];

  // ── Wizard session — S482 Phase E ─────────────────────────────────────────
  const { data: wizardSessionsData } = useFetch(
    fiscalPeriodId
      ? `${API_BASE}/wizard-sessions?fiscal_period_id=${fiscalPeriodId}&active_only=true`
      : null
  );
  const wizardSessionId = wizardSessionsData?.data?.[0]?.wizard_session_id ?? null;

  // ── Tenant context — S77 tier gate ────────────────────────────────────────
  const { data: tenantContext } = useFetch(`${API_BASE}/tenant-context`);
  const aiQueryEnabled = tenantContext?.features?.allows_ai_query ?? null;

  const location  = useLocation();
  const pageTitle = PAGE_TITLES[location.pathname] ?? "Period Dashboard";

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      <TopBar
        userName={user?.full_name ?? user?.email ?? null}
        userRole={user?.user_type ?? "finance"}
        userRoleLabel="Finance"
        mode={import.meta.env.VITE_APP_MODE === "dev" ? "dev" : "prod"}
        onMenuOpen={() => setMobileOpen(true)}
        copilotOpen={copilotOpen}
        onToggleCopilot={() => setCopilotOpen((v) => !v)}
      />

      <div className="flex min-h-[calc(100vh-64px)]">
        <FinanceNav mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          {/* Context header — period selector at shell level, mirrors CompAdmin */}
          <FinanceContextHeader
            pageTitle={pageTitle}
            periods={periods}
            selectedPeriodId={selectedPeriodId}
            defaultPeriod={defaultPeriod}
            isNonDefaultPeriod={isNonDefaultPeriod}
            period={period}
            onPeriodSelect={setSelectedPeriodId}
            hasApprovedPlanInput={hasApprovedPlanInput}
          />

          <Routes>
            {/* S608: 4-page CFO workflow. Index → Period Dashboard. */}
            <Route index element={<Navigate to="dashboard" replace />} />

            {/* ── Primary pages ── */}
            <Route path="dashboard" element={
              <PeriodDashboard
                period={period}
                allBatches={allBatches}
                allPlanInputs={allPlanInputs}
                allAopData={allAopRows}
                allAlignmentData={allAlignmentRows}
                allExposureByRole={allExposureByRole}
                allPlanVsLoaded={allPlanVsLoaded}
              />
            } />
            <Route path="governance" element={
              <PlanGovernance
                period={period}
                fiscalPeriodId={fiscalPeriodId}
                allPlanInputs={allPlanInputs}
                allAopData={allAopRows}
              />
            } />
            <Route path="close" element={
              <PeriodClose fiscalPeriodId={fiscalPeriodId} />
            } />
            <Route path="scenarios" element={
              <div className="space-y-6">
                {/* S1039 — ExposureMonitor first: its warning message says "Create one
                    in the Scenario section below" — ScenarioSetBuilder IS that section.
                    Order: ExposureMonitor (run exposure + warning) → ScenarioSetBuilder
                    (Finance creates set) → CA approves → Compute Exposure enables. */}
                <ScenarioSetBuilder
                  fiscalPeriodId={fiscalPeriodId}
                  fiscalPeriodLabel={period?.period_label ?? period?.period_code ?? null}
                  wizardSessionId={wizardSessionId}
                />
                <ExposureMonitor
                  allPlanInputs={allPlanInputs}
                  fiscalPeriodId={fiscalPeriodId}
                  wizardSessionId={wizardSessionId}
                />
              </div>
            } />

            {/* ── S801 New-architecture routes — old routes above preserved until proven ── */}
            <Route path="financial-control" element={
              <CompFinancialControl fiscalPeriodId={fiscalPeriodId} currentUser={user} />
            } />
            <Route path="period-close-v2" element={
              <PeriodCloseWorkflow fiscalPeriodId={fiscalPeriodId} currentUser={user} />
            } />
            <Route path="settlements" element={
              <SettlementLedger fiscalPeriodId={fiscalPeriodId} />
            } />

            {/* ── Legacy route redirects — all old paths preserved, no broken links ── */}
            <Route path="alignment"        element={<Navigate to="/finance/dashboard"  replace />} />
            <Route path="plan-inputs"      element={<Navigate to="/finance/governance" replace />} />
            <Route path="calculation-runs" element={<Navigate to="/finance/close"      replace />} />
            <Route path="population-scope" element={<Navigate to="/finance/dashboard"  replace />} />
            <Route path="statements"       element={<Navigate to="/finance/close"      replace />} />
            <Route path="payroll"          element={<Navigate to="/finance/close"      replace />} />
            <Route path="reconciliation"   element={<Navigate to="/finance/close"      replace />} />
            <Route path="disputes"         element={<Navigate to="/finance/close"      replace />} />
            <Route path="exposure-monitor" element={<Navigate to="/finance/scenarios"  replace />} />
            <Route path="scenario-planner" element={<Navigate to="/finance/scenarios"  replace />} />
            <Route path="scenario-history" element={<Navigate to="/finance/scenarios"  replace />} />
            <Route path="plan-versions"    element={<Navigate to="/finance/governance" replace />} />
            <Route path="*"                element={<Navigate to="/finance/dashboard"  replace />} />
          </Routes>
        </main>

        {/* Copilot — shared CopilotPanel, S241 wiring. Sibling to main, not child. */}
        <CopilotPanel
          open={copilotOpen}
          fiscalPeriodId={fiscalPeriodId}
          stageId={null}
          stageName={null}
          aiQueryEnabled={aiQueryEnabled}
        />
      </div>

      {/* Mobile copilot button */}
      <div className="fixed bottom-4 right-4 xl:hidden">
        <Button onClick={() => setCopilotOpen(!copilotOpen)}>
          <Sparkles className="h-4 w-4" /> Copilot
        </Button>
      </div>
    </div>
  );
}
